import Photoswipe from '@archetype-themes/scripts/modules/photoswipe'

// Open IndexedDB
let db;
const DB_NAME = "UploadPortalDB";
const DB_VERSION = 7;
const STORE_NAME = "templates";
const STORE_SENT = "sentProducts";
const MODE_STATE_KEY = "uploadPortalModeState";
const PORTAL_TUTORIAL_KEY = "uploadPortalSidebarTutorialSeen";
const PORTAL_TUTORIAL_STATE_KEY = "uploadPortalTutorialState";
const PORTAL_TUTORIAL_TOTAL_STEPS = 11;

const templateFilesMap = new WeakMap();

// Convert a (optionally data-URI prefixed) base64 string into a File
function base64ToFile(base64String, fileName, mimeType = 'application/pdf') {
    try {
        const base64Data = base64String.includes(',') && base64String.trim().startsWith('data:')
            ? base64String.split(',')[1]
            : base64String;

        const byteChars = atob(base64Data);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        return new File([byteArray], fileName, { type: mimeType });
    } catch (e) {
        console.error('Failed to convert base64 repaired_pdf to file:', e);
        return null;
    }
}

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };
        request.onupgradeneeded = (event) => {
            db = event.target.result;

            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "id" });
            }

            if (!db.objectStoreNames.contains(STORE_SENT)) {
                db.createObjectStore(STORE_SENT, { keyPath: "id" });
            }
        };
    });
}

function markProductSent(orderId, productId) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_SENT, "readwrite");
        const store = tx.objectStore(STORE_SENT);
        const request = store.put({ id: `${orderId}_${productId}`, sent: true });

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
    });
}

function isProductSent(orderId, productId) {
    return new Promise(resolve => {
        const tx = db.transaction(STORE_SENT, "readonly");
        const store = tx.objectStore(STORE_SENT);
        const request = store.get(`${orderId}_${productId}`);
        request.onsuccess = () => resolve(request.result ? true : false);
        request.onerror = () => resolve(false);
    });
}

function saveModeState(checked, value = 'canva') {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(STORE_SENT, "readwrite");
            const store = tx.objectStore(STORE_SENT);
            const request = store.put({
                id: MODE_STATE_KEY,
                checked: Boolean(checked),
                value: String(value || 'canva')
            });

            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        } catch (e) {
            reject(e);
        }
    });
}

function getModeState() {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(STORE_SENT, "readonly");
            const store = tx.objectStore(STORE_SENT);
            const request = store.get(MODE_STATE_KEY);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => resolve(null);
        } catch (e) {
            resolve(null);
        }
    });
}

function getModeCheckbox() {
    return document.querySelector('.mode-toggle__input') ||
        document.querySelector('input[type="checkbox"][name="mode"]') ||
        document.querySelector('input[type="checkbox"][value="canva"]') ||
        document.querySelector('#mode-canva');
}

async function initializeModeCheckboxState() {
    const modeCheckbox = getModeCheckbox();
    if (!modeCheckbox) return;

    const savedModeState = await getModeState();
    if (savedModeState && typeof savedModeState.checked !== 'undefined') {
        modeCheckbox.checked = Boolean(savedModeState.checked);
    }

    modeCheckbox.addEventListener('change', () => {
        saveModeState(modeCheckbox.checked, modeCheckbox.value || 'canva').catch((err) => {
            console.error('Failed saving mode state:', err);
        });
    });
}

function saveTemplate(orderId, productId, templateNumber, fileName, quantity, file = null, validation = null, folderKey = '', page = null, repairedPdfFileId = '') {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(STORE_NAME, "readwrite");
            const store = tx.objectStore(STORE_NAME);
            const key = `${orderId}_${productId}_${templateNumber}`;

            const getReq = store.get(key);
            getReq.onsuccess = () => {
                const existing = getReq.result || {};

                const templateData = Object.assign({}, existing, {
                    id: key,
                    fileName: fileName ?? existing.fileName,
                    quantity: (typeof quantity !== 'undefined' ? quantity : existing.quantity),
                    folder_key: folderKey ?? existing.folder_key,
                    page: page ?? existing.page,
                    repaired_pdf_file_id: repairedPdfFileId ?? existing.repaired_pdf_file_id
                });

                if (file) templateData.fileBlob = file;
                if (validation) templateData.validation = validation;

                const putReq = store.put(templateData);
                putReq.onsuccess = () => resolve(templateData);
                putReq.onerror = () => reject(putReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        } catch (e) {
            reject(e);
        }
    });
}

function getTemplates(orderId, productId) {
    return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const templates = [];
        store.openCursor().onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                if (cursor.value.id.startsWith(`${orderId}_${productId}_`)) {
                    templates.push(cursor.value);
                }
                cursor.continue();
            } else {
                resolve(templates);
            }
        };
    });
}

function getTemplateBlob(orderId, productId, templateNumber) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(`${orderId}_${productId}_${templateNumber}`);
        request.onsuccess = () => {
            const result = request.result;
            if (result && result.fileBlob) {
                resolve(result.fileBlob);
            } else {
                resolve(null);
            }
        };
        request.onerror = () => reject(request.error);
    });
}

function deleteTemplateFromDB(orderId, productId, templateNumber) {
    return new Promise((resolve, reject) => {
        try {
            const tx = db.transaction(STORE_NAME, "readwrite");
            const store = tx.objectStore(STORE_NAME);
            const key = `${orderId}_${productId}_${templateNumber}`;
            const request = store.delete(key);
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error);
        } catch (e) {
            reject(e);
        }
    });
}

function saveTemplateSelection(orderId, productId, templateNumber, selectedTemplate) {
    return new Promise((resolve, reject) => {
        try {
            
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const key = `${orderId}_${productId}_${templateNumber}`;
            const getReq = store.get(key);
            getReq.onsuccess = () => {
                const existing = getReq.result || {};
                const updated = Object.assign({}, existing, { selectedTemplate });
                const putReq = store.put(updated);
                putReq.onsuccess = () => resolve(updated);
                putReq.onerror = () => reject(putReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        } catch (e) {
            console.log(e);
            reject(e);
        }
    });
}

async function validateFileTemplate() {}

function extractTemplateNameFromUrl(url) {
    // Extracteer alles na de LAATSTE /files/ en voor ?
    try {
        const withoutQuery = url.split('?')[0];
        const fileName = withoutQuery.split('/').pop();
        return fileName || url;
    } catch (e) {
        return url;
    }
}

function extractTemplateAmount(templateName) {
    try {
        const match = templateName.match(/^(?:SP-)?(\d+)-/i);
        return match ? parseInt(match[1], 10) : 1;
    } catch (e) {
        return 1;
    }
}

function formatTemplateDisplayName(templateName) {
    const nameWithoutExtension = String(templateName || '').replace(/\.pdf$/i, '');
    const segments = nameWithoutExtension.split(/[-_]/).filter(Boolean);
    const spIndex = segments.findIndex(segment => String(segment || '').toUpperCase() === 'SP');

    if (spIndex !== -1 && segments.length >= (spIndex + 6)) {
        const spDisplaySegments = [
            segments[spIndex + 2],
            segments[spIndex + 4],
            ...segments.slice(spIndex + 5)
        ].filter(Boolean);

        return spDisplaySegments.join(' ').trim();
    }

    const templateNameSplit = nameWithoutExtension.split('-');
    const offset = !isNaN(parseInt(templateNameSplit[0], 10)) ? 1 : 0;
    return (templateNameSplit[offset] || nameWithoutExtension).replace(/_/g, ' ').trim();
}

function isSpTemplate(templateName) {
    const nameWithoutExtension = String(templateName || '').replace(/\.pdf$/i, '');
    return /(^|[-_\s])SP($|[-_\s])/i.test(nameWithoutExtension);
}

function isInfoTemplate(templateName) {
    return String(templateName || '').toUpperCase().startsWith('INFO');
}

function isUploadTemplate(templateName) {
    return !isSpTemplate(templateName) && !isInfoTemplate(templateName);
}

function getProductQuantity(product) {
    try {
        if (product && product.properties && typeof product.properties['aantal'] !== 'undefined' && product.properties['aantal'] !== null && String(product.properties['aantal']).trim() !== '') {
            const n = Number(product.properties['aantal']);
            if (!isNaN(n) && isFinite(n)) return n;
        }

        if (product && typeof product.quantity !== 'undefined' && product.quantity !== null && String(product.quantity).trim() !== '') {
            const q = Number(product.quantity);
            if (!isNaN(q) && isFinite(q)) return q;
        }
    } catch (e) {
    }
    return 1;
}



// ---- MAIN ----

let products = [];
let templateStats = {};
let maxDesigns = 0;
let addBtn = null;
let saveBtn = null;
let portalTutorialPositionRetryId = null;
let portalTutorialScrollUnlockRestoreId = null;
let currentPortalTutorialStepIndex = 0;
let portalTutorialForcedVisibleEl = null;

const PORTAL_TUTORIAL_UPLOAD_LIST_PLACEHOLDER_CLASS = 'portal-tutorial-upload-list-placeholder';
let normalizedShopLang = 'nl';

async function loadOrder() {
    await openDB();
    await initializeModeCheckboxState();

    const params = new URLSearchParams(window.location.search);

    // Probeer eerst orderId, anders orderID
    const orderId = params.get("orderId") || params.get("orderID");
    const urlId = params.get("id");

    const rawShopLang =
        (window.Shopify && (window.Shopify.locale || window.Shopify.language)) ||
        document.documentElement.lang ||
        '';

    normalizedShopLang = rawShopLang
        .toString()
        .split('-')[0]
        .toLowerCase();

    const shopLang = normalizedShopLang === 'nl' ? '' : (normalizedShopLang || '');

    const url = `https://kartent-app.monkeydo.it/order?orderId=${orderId}&id=${urlId}&lang=${encodeURIComponent(shopLang)}`;
    console.log("🔵 FETCH URL:", url);

    const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "omit"
    });

    const debugScenario = (params.get('debugScenario') || params.get('testScenario') || '').toLowerCase();
    const debugPaymentStatus = params.get('debugPaymentStatus');
    const debugCreatedAt = params.get('debugCreatedAt');
    const debugAgeMinutes = params.get('debugAgeMinutes');
    const debugOrderUploadComplete = params.get('debugOrderUploadComplete');
    const debugNoTemplates = params.get('debugNoTemplates');

    const applyDebugOrderOverrides = (orderResult) => {
        if (!debugScenario && !debugPaymentStatus && !debugCreatedAt && !debugAgeMinutes && !debugOrderUploadComplete && !debugNoTemplates) {
            return orderResult;
        }

        const debugResult = Object.assign({}, orderResult);
        const minutesAgo = Number(debugAgeMinutes);
        const hasNoTemplates = ['1', 'true', 'yes'].includes(String(debugNoTemplates || '').toLowerCase());
        const isUploadCompleteOverride = ['1', 'true', 'yes'].includes(String(debugOrderUploadComplete || '').toLowerCase());
        const createdAtFromMinutes = Number.isFinite(minutesAgo)
            ? new Date(Date.now() - (minutesAgo * 60 * 1000)).toISOString()
            : null;

        const applyNoTemplateState = () => {
            debugResult.data = [];
            debugResult.orderUploadComplete = false;
        };

        switch (debugScenario) {
            case 'unpaid':
            case 'unpaid_no_templates':
                debugResult.payment_status = 'UNPAID';
                debugResult.created_at = new Date(Date.now() - (30 * 60 * 1000)).toISOString();
                applyNoTemplateState();
                break;
            case 'paid_soon':
            case 'paid_soon_no_templates':
                debugResult.payment_status = 'PAID';
                debugResult.created_at = new Date(Date.now() - (5 * 60 * 1000)).toISOString();
                applyNoTemplateState();
                break;
            case 'paid_late':
            case 'paid_late_no_templates':
                debugResult.payment_status = 'PAID';
                debugResult.created_at = new Date(Date.now() - (20 * 60 * 1000)).toISOString();
                applyNoTemplateState();
                break;
            case 'success':
            case 'upload_complete':
                debugResult.orderUploadComplete = true;
                break;
            case 'templates':
                debugResult.data = Array.isArray(debugResult.data) && debugResult.data.length > 0
                    ? debugResult.data
                    : [{
                        id: 'debug-template',
                        title: 'Debug product',
                        variantTitle: '',
                        quantity: 1,
                        image: null,
                        template_urls: ['https://example.com/debug.pdf'],
                        properties: [],
                        completed: false
                    }];
                break;
        }

        if (debugPaymentStatus) {
            debugResult.payment_status = debugPaymentStatus;
        }

        if (debugCreatedAt) {
            debugResult.created_at = debugCreatedAt;
        } else if (createdAtFromMinutes) {
            debugResult.created_at = createdAtFromMinutes;
        }

        if (isUploadCompleteOverride) {
            debugResult.orderUploadComplete = true;
        }

        if (hasNoTemplates) {
            applyNoTemplateState();
        }

        return debugResult;
    };

    const result = applyDebugOrderOverrides(await response.json());
    products = Array.isArray(result.data) ? result.data : [];

    console.log('result: ', result);
    console.log('products: ', products);

    if (result && result.orderUploadComplete === true) {
        document.getElementById("upload-wrapper").classList.add("hidden");
        document.getElementById("portal-footer").classList.add("hidden");
        document.getElementById("loading-wrapper").classList.add("hidden");
        document.getElementById("success-wrapper").classList.remove("hidden");
        return;
    }

    const paymentStatus = String(result.payment_status || '').toUpperCase();
    const createdAtMs = new Date(result.created_at || '').getTime();
    const isPaid = paymentStatus === 'PAID';
    const isWithinTenMinutes = isPaid && Number.isFinite(createdAtMs) && (Date.now() - createdAtMs) <= (10 * 60 * 1000);
    const isDebugPaidSoonScenario = ['paid_soon', 'paid_soon_no_templates'].includes(debugScenario);
    const isDebugPaidLateScenario = ['paid_late', 'paid_late_no_templates'].includes(debugScenario);

    const noTemplatesMessageKey = !isPaid
        ? 'noTemplatesOrderUnpaidMessage'
        : (isDebugPaidLateScenario
            ? 'noTemplatesOrderPaidLateMessage'
            : (isDebugPaidSoonScenario || isWithinTenMinutes
                ? 'noTemplatesOrderPaidSoonMessage'
                : 'noTemplatesOrderPaidLateMessage'));

    const noTemplatesMessageText = !isPaid
        ? window.UploadPortalTranslations.noTemplatesOrderUnpaidMessage
        : (isDebugPaidLateScenario
            ? window.UploadPortalTranslations.noTemplatesOrderPaidLateMessage
            : (isDebugPaidSoonScenario || isWithinTenMinutes
                ? window.UploadPortalTranslations.noTemplatesOrderPaidSoonMessage
                : window.UploadPortalTranslations.noTemplatesOrderPaidLateMessage));

    const buildNoTemplatesNoticeMessage = (messageKey, messageText) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'upload-portal__notice-text';

        if (messageKey === 'noTemplatesOrderUnpaidMessage' || messageKey === 'noTemplatesOrderPaidLateMessage') {
            const [beforeEmail, afterEmail = ''] = String(messageText || '').split('{email}');
            paragraph.append(document.createTextNode(beforeEmail));

            const emailLink = document.createElement('a');
            emailLink.href = 'mailto:webshop@kartent.com';
            emailLink.textContent = 'webshop@kartent.com';
            emailLink.className = 'upload-portal__notice-link';
            paragraph.append(emailLink);

            paragraph.append(document.createTextNode(afterEmail));
        } else {
            paragraph.textContent = String(messageText || '');
        }

        return paragraph;
    };

    const showNoTemplatesNotice = (messageKey, messageText) => {
        const uploadWrapper = document.getElementById("upload-wrapper");
        const loadingWrapper = document.getElementById("loading-wrapper");
        const successWrapper = document.getElementById("success-wrapper");
        const successInner = successWrapper.querySelector('.success-wrapper-inner');

        uploadWrapper.classList.add("hidden");
        loadingWrapper.classList.add("hidden");

        if (successInner) {
            successInner.innerHTML = '';

            const icon = document.createElement('div');
            icon.className = 'success-icon';
            icon.setAttribute('aria-hidden', 'true');

            if (messageKey === 'noTemplatesOrderPaidSoonMessage') {
                icon.classList.add('upload-portal__hourglass-icon');
                icon.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" height="70px" viewBox="0 -960 960 960" width="70px" fill="currentColor">
                        <path d="M320-160h320v-120q0-66-47-113t-113-47q-66 0-113 47t-47 113v120Zm273-407q47-47 47-113v-120H320v120q0 66 47 113t113 47q66 0 113-47ZM160-80v-80h80v-120q0-61 28.5-114.5T348-480q-51-32-79.5-85.5T240-680v-120h-80v-80h640v80h-80v120q0 61-28.5 114.5T612-480q51 32 79.5 85.5T720-280v120h80v80H160Zm320-80Zm0-640Z"/>
                    </svg>
                `;
            } else {
                icon.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                `;
            }

            const content = document.createElement('div');
            content.className = 'upload-portal__notice-content';
            content.appendChild(buildNoTemplatesNoticeMessage(messageKey, messageText));

            successInner.appendChild(icon);
            successInner.appendChild(content);
        }

        successWrapper.classList.remove("hidden");
    };

    const orderIsEmpty = !result.data || result.data.length === 0;

    if (orderIsEmpty) {
        showNoTemplatesNotice(noTemplatesMessageKey, noTemplatesMessageText);
        return;
    }

    document.getElementById("order-number").textContent = "#" + orderId;
    const productList = document.getElementById("product-list");
    productList.innerHTML = "";

    const pendingProducts = [];

    for (let i = 0; i < products.length; i++) {
        const product = products[i];

        let visibleTemplates = [];
        if (product.template_urls && Array.isArray(product.template_urls)) {
            visibleTemplates = product.template_urls.filter(tpl => {
                const templateName = extractTemplateNameFromUrl(tpl);
                return isUploadTemplate(templateName);
            });
        }

        const hasPrintFiles = visibleTemplates.length > 0;
        const sent = Boolean(product.completed);

        product._hasNoTemplates = !hasPrintFiles;
        product._hasPrintFiles = hasPrintFiles;
        product._isSent = sent;

        if (!sent && hasPrintFiles) {
            pendingProducts.push(product);
        }
    }

    const hasAnyTemplates = products.some(product => product._hasPrintFiles);

    if (!hasAnyTemplates) {
        showNoTemplatesNotice(noTemplatesMessageKey, noTemplatesMessageText);
        return;
    }

    console.log('pending products:', pendingProducts);

    if (pendingProducts.length === 0) {
        document.getElementById("upload-wrapper").classList.add("hidden");
        document.getElementById("portal-footer").classList.add("hidden");
        document.getElementById("loading-wrapper").classList.add("hidden");
        document.getElementById("success-wrapper").classList.remove("hidden");
        return; 
    }

    const firstActiveIndex = products.findIndex(p => p._hasPrintFiles && !p._isSent);
    const chosenIndex = (firstActiveIndex !== -1) ? firstActiveIndex : (products.length > 0 ? 0 : -1);

    products.forEach((product, index) => {
        // Keep items that have no templates visible in the sidebar (but disabled).
        // Only skip rendering products that are sent AND that do have templates.
        if (product._isSent && !product._hasNoTemplates) return;

        const visibleTemplateUrls = Array.isArray(product.template_urls)
            ? product.template_urls.filter(tpl => {
                const templateName = extractTemplateNameFromUrl(tpl);
                return isUploadTemplate(templateName);
            })
            : [];
        const templateCount = visibleTemplateUrls.length;
        const quantity = getProductQuantity(product);

        let productImageHtml = product.image
            ? `<img src="${product.image}" />`
            : `<div class="placeholder-image"></div>`;

        productList.innerHTML += `
            <div class="sidebar__product product ${index === chosenIndex ? "active" : ""} ${templateCount === 0 ? 'has-no-templates' : 'has-templates'}" data-index="${index}" data-id="${product.id}">
                <div class="product__status-indicator indicator-pending"></div>
                <div class="product__image">
                    ${productImageHtml}
                </div>
                <div class="product__details">
                    <h6 class="product__name">${product.title}</h6>
                    <div class="product__variant-name">${product.variantTitle}</div>
                    <ul class="product__propertylist">
                        <li class="product__property-title">${window.UploadPortalTranslations.quantityLabel} <span>${quantity}</span></li>
                        <li class="product__property-title">${window.UploadPortalTranslations.templatesLabel} <span>${templateCount}</span></li>
                    </ul>
                </div>
            </div>
        `;
    });

    if (chosenIndex !== -1) {
        loadWorkspace(products[chosenIndex], orderId);
    }

    document.querySelectorAll(".sidebar__product").forEach((item) => {
        item.addEventListener("click", () => {
            if (item.classList.contains('has-no-templates')) return;

            const i = Number(item.dataset.index);
            if (!Number.isFinite(i) || !products[i]) return;

            loadWorkspace(products[i], orderId);

            document.querySelectorAll(".sidebar__product")
                .forEach((el) => el.classList.remove("active"));
            item.classList.add("active");
        });
    });
}

async function loadWorkspace(product, orderId) {
    const workspaceTitle = document.getElementById("workspace-product-title");
    const workspaceVariantTitle = document.getElementById("workspace-product-variant-title");
    const downloadLinks = document.getElementById("template-download-links");
    const uploadList = document.getElementById("template-upload-list");
    addBtn = document.getElementById("add-template-btn");
    saveBtn = document.getElementById("save-templates-btn");
    const uploadWrapper = document.getElementById("upload-wrapper");
    const loadingWrapper = document.getElementById("loading-wrapper");

    
    if (!Array.isArray(product.template_urls)) product.template_urls = [];

    workspaceTitle.textContent = product.title;

    workspaceVariantTitle.textContent = product.variantTitle;


    const quantity = getProductQuantity(product);


    const visibleTemplateUrls = product.template_urls.filter(tpl => {
        const templateName = extractTemplateNameFromUrl(tpl);
        return isUploadTemplate(templateName);
    });


    maxDesigns = 0;
   
    visibleTemplateUrls.forEach((tpl, index) => {
        const templateName = extractTemplateNameFromUrl(tpl);
        const templateAmount = extractTemplateAmount(templateName);

        maxDesigns += (templateAmount || 1) * quantity;
    });


    const infoDownloadTemplateUrls = product.template_urls.filter(tpl => {
        const templateName = extractTemplateNameFromUrl(tpl);
        return isInfoTemplate(templateName);
    });

    const spTemplateUrls = product.template_urls.filter(tpl => {
        const templateName = extractTemplateNameFromUrl(tpl);
        return isSpTemplate(templateName);
    });

    const regularTemplateUrls = product.template_urls.filter(tpl => {
        const templateName = extractTemplateNameFromUrl(tpl);
        return isUploadTemplate(templateName);
    });

    const orderedTemplateUrls = [...regularTemplateUrls, ...spTemplateUrls];

    const infoFileListItemsHtml = infoDownloadTemplateUrls
        .map(tpl => {
            const templateName = extractTemplateNameFromUrl(tpl);
            const cleanTemplateName = templateName
                .replace(/\.pdf$/i, '')
                .replace(/^INFO[-_\s]*/i, '')
                .replace(/_/g, ' ')
                .trim();

            const displayName = cleanTemplateName || templateName.replace(/\.pdf$/i, '').replace(/_/g, ' ').trim();

            return `
                <li class="workspace__info-file-item">
                    <a href="${tpl}"
                        class="workspace__info-file-link"
                        download
                        target="_blank"
                        data-template-name="${templateName}">
                            <svg class="workspace__info-file-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
                                <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"/>
                            </svg>
                            <span class="workspace__info-file-label">${displayName}</span>
                    </a>
                </li>
            `;
        })
        .join('');

    const templateRowsHtml = orderedTemplateUrls
    .map((tpl, i) => {
        const templateName = extractTemplateNameFromUrl(tpl);
        const isSpInfoTemplate = isSpTemplate(templateName);
        const displayTemplateName = formatTemplateDisplayName(templateName);

        const templateAmount = extractTemplateAmount(templateName);

        const counterHtml = !isSpInfoTemplate
            ? `
                <div class="workspace__template_stat template__counter" data-template="${tpl}">
                    <span class="template_amount counter-value">0/${quantity * templateAmount}</span>
                </div>
            `
            : `
                <div class="workspace__template_stat template__counter is-complete" data-template="${tpl}" data-fixed-counter="true">
                    <span class="template_amount counter-value">${quantity * templateAmount}/${quantity * templateAmount}</span>
                </div>
            `;

        const actionButtons = !isSpInfoTemplate
            ? `
                <a href="${tpl}"
                    class="download-btn"
                    download
                    target="_blank"
                    data-templateName="${templateName}">
                        ${window.UploadPortalTranslations.downloadTemplate}
                </a>

                <a class="download-btn edit-btn"
                    href="https://editor.kartent.com//?template=${tpl}"
                    target="_blank">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.269 5.614l-1.883-1.883a2.5 2.5 0 0 0-3.531 0l-9.562 9.562a1 1 0 0 0-.242.391l-2 6a1 1 0 0 0 1.265 1.265l6-2a1 1 0 0 0 .391-.242l9.562-9.562a2.5 2.5 0 0 0 0-3.531zM7.414 14L14 7.414 16.586 10 10 16.586zm-.977 1.851l1.712 1.712-2.568.856zm12.417-8.12L18 8.586 15.414 6l.854-.854a.5.5 0 0 1 .7 0l1.883 1.883a.5.5 0 0 1 .003.702z"/></svg>
                        ${window.UploadPortalTranslations.editButton || window.UploadPortalTranslations.edit_button || 'Bestand opmaken'}
                </a>

                <a href="javascript:;" id="upload-file" class="btn">
                    <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#fff"><path d="M440-320v-326L336-542l-56-58 200-200 200 200-56 58-104-104v326h-80ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"/></svg>
                    <span>${window.UploadPortalTranslations && (window.UploadPortalTranslations.uploadButton || window.UploadPortalTranslations.upload_button) ? (window.UploadPortalTranslations.uploadButton || window.UploadPortalTranslations.upload_button) : 'Bestand uploaden'}</span>
                </a>
            `
            : `
                <span class="template-info-text">${window.UploadPortalTranslations.templateInfoText}</span>
            `;

        return `
            <div class="workspace-item" data-href="${tpl}">
                <div class="doc-preview"></div>
                <div class="template-name">${displayTemplateName}</div>

                ${counterHtml}
                ${actionButtons}
            
            </div>
        `;
    })
    .join("");

    downloadLinks.innerHTML = `
        ${infoFileListItemsHtml ? `
            <div class="workspace__info-downloads">
                <div class="workspace__info-downloads-title">Informatiebestanden</div>
                <ul class="workspace__info-downloads-list">${infoFileListItemsHtml}</ul>
            </div>
        ` : ''}
        <div class="workspace__template-table">${templateRowsHtml}</div>
    `;

    // Maak eenvoudige PDF thumbnails voor de download-knoppen
    // (fetch de PDF en render de eerste pagina in .doc-preview)
    setTimeout(() => {
        try {
            document.querySelectorAll('#template-download-links .workspace-item').forEach(a => {
                const href = a.getAttribute('data-href');
                console.log(href)
                const preview = a.querySelector('.doc-preview');
                const templateNameloc = a.dataset.templatename;
                if (!href || !preview) return;

                const hrefNoQuery = href.split('?')[0];
                if (hrefNoQuery.toLowerCase().endsWith('.pdf')) {
                    try { generatePdfThumbnailFromUrl(href, preview, 300).catch(()=>{}); } catch (e) { /* ignore */ }
                }

            });
        } catch (e) {
            /* ignore */
        }
    }, 0);

    // reset stats object
    templateStats = {};

    visibleTemplateUrls.forEach((tpl, i) => {
        templateStats[tpl] = 0;
    });

    let amount = 0;
    uploadList.innerHTML = "";

    const savedTemplates = await getTemplates(orderId, product.id);

    function addTemplate(number, fileName = null, quantity = 1) {
        const templateDiv = document.createElement("div");
        templateDiv.classList.add("workspace__template", "template");

        let rightInner = `
            <a href="javascript:;" class="template__remove" onclick="removeTemplate(this)">
                <img src="https://cdn.shopify.com/s/files/1/0840/3759/9498/files/trash-fill.svg?v=1765292249" width="18px">
            </a>
        `;

        let infoInner = `<div class="template__filename-title">Geen bestand gekoppeld</div>`;

        if (fileName) {
            infoInner = `
            <div class="template__thumb-placeholder"></div>
            <div class="template__filename">${fileName}</div>
            <div class="template__validation-results"></div>
            `;

            const selectOptions = `
                <option value="">${window.UploadPortalTranslations && (window.UploadPortalTranslations.chooseTemplate || window.UploadPortalTranslations.choose_template) ? (window.UploadPortalTranslations.chooseTemplate || window.UploadPortalTranslations.choose_template) : 'Kies een template'}</option>
                ${visibleTemplateUrls
                    .map((tpl, idx) => {
                        const templateName = extractTemplateNameFromUrl(tpl);
                        const templateNameSplit = templateName.split('-');
                        const offset = !isNaN(parseInt(templateNameSplit[0])) ? 1 : 0;
                        const displayName = (templateNameSplit[offset] || `Template ${idx + 1}`).replace(/\.pdf$/i, '').replace(/_/g, ' ');
                        return `<option value="${tpl}">${displayName}</option>`;
                    })
                    .join("")}
            `;

            rightInner = `
                <div class="template__selection">
                    <select class="template-select">
                        ${selectOptions}
                    </select>
                </div>
                <div class="template__quantity invisible">
                    <button class="qty-btn minus">−</button>
                    <input type="number" class="qty-input" value="${quantity}" min="1" />
                    <button class="qty-btn plus">+</button>
                </div>
               <a href="javascript:;" class="template__remove" onclick="removeTemplate(this)">
                    <img src="https://cdn.shopify.com/s/files/1/0840/3759/9498/files/trash-fill.svg?v=1765292249" width="18px">
                </a>
            `;
        }

        templateDiv.innerHTML = `
            <div class="template__inner">
            <div class="template__info">${infoInner}</div>
            <div class="template__right">${rightInner}</div>
            </div>
        `;

    uploadList.insertBefore(templateDiv, uploadList.firstChild);

    templateDiv.dataset.templateNumber = number;

        const removeBtn = templateDiv.querySelector(".template__remove");
        if (removeBtn) {
            removeBtn.addEventListener("click", () => {
                templateDiv.remove();
                updateTemplateStats();
                updateUploadedAmount();
            });
        }

        updateUploadedAmount();
        return templateDiv;
    }

    let maxSaved = 0;
    savedTemplates.forEach(t => {
        const templateNumber = Number(t.id.split('_')[2]);
        if (!isNaN(templateNumber)) {
            const created = addTemplate(templateNumber, t.fileName, t.quantity);
                    if (t.fileBlob && created) {
                    templateFilesMap.set(created, t.fileBlob);
                    // Probeer meteen een thumbnail te genereren voor opgeslagen PDF's
                    try { generatePdfThumbnail(t.fileBlob, created).catch(()=>{}); } catch(e) { /* ignore */ }
                }
            if (created) {
                bindTemplateEvents(created, templateNumber, t.fileName, orderId, product, maxDesigns);
                created.dataset.orderId = orderId;
                created.dataset.productId = product.id;
                created.dataset.templateNumber = templateNumber;

                created.dataset.templateFolderKey = t.folder_key || '';
                created.dataset.templatePage = t.page || '';
                created.dataset.repairedPdfFileId = t.repaired_pdf_file_id || '';

                if (t.validation) {
                    try {
                        renderValidationResults(created, t.validation);
                    } catch (e) {
                        console.error('Fout bij renderen opgeslagen validatieresultaten:', e);
                    }
                }

                if (t.selectedTemplate) {
                    const sel = created.querySelector('.template-select');
                    const qtyWrap = created.querySelector('.template__quantity');
                    if (sel) sel.value = t.selectedTemplate;
                    created.dataset.selectedTemplate = t.selectedTemplate;
                    if (qtyWrap) qtyWrap.classList.remove('invisible');
                    updateTemplateStats();
                }
            }
            if (templateNumber > maxSaved) maxSaved = templateNumber;
        }
    });
    amount = Math.max(amount, maxSaved);

    // Ensure statistics and the save button reflect restored templates
    try {
        updateTemplateStats();
        updateUploadedAmount();
    } catch (e) {
        console.error('Error updating stats after restoring templates:', e);
    }

    if (addBtn) {
        addBtn.style.display = 'none';
        addBtn.onclick = null;
        addBtn.dataset.disabled = 'true';
    }

    const autoAssignTemplateWithValidation = (templateDiv, preferredTemplateUrl = '') => {
        if (!templateDiv) return;

        const selectEl = templateDiv.querySelector('.template-select');
        if (!selectEl) return;

        const hasPreferredTemplate =
            preferredTemplateUrl &&
            Array.isArray(visibleTemplateUrls) &&
            visibleTemplateUrls.includes(preferredTemplateUrl);

        const templateToAssign = hasPreferredTemplate
            ? preferredTemplateUrl
            : (Array.isArray(visibleTemplateUrls) && visibleTemplateUrls.length === 1
                ? visibleTemplateUrls[0]
                : '');

        if (!templateToAssign) return;

        selectEl.value = templateToAssign;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const bindUploadFileButton = (uploadFileBtn) => {
        uploadFileBtn.addEventListener('click', (event) => {
            event.preventDefault();

            if (uploadFileBtn.getAttribute('aria-disabled') === 'true' || uploadFileBtn.classList.contains('is-disabled')) {
                return;
            }

            const isTemplateButton = Boolean(uploadFileBtn.closest('.workspace-item'));
            const allowMultipleUpload = true;
            const templateButtonUrl = uploadFileBtn.closest('.workspace-item')?.getAttribute('data-href') || '';

            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.pdf';
            input.multiple = allowMultipleUpload;
            input.addEventListener('change', async () => {
                const files = input.files;
                const file = input.files[0];

                if (!file) return;

                // Prevent uploading if workspace is already full
                const remainingBefore = (typeof maxDesigns !== 'undefined' && maxDesigns !== null) ? (Number(maxDesigns) - getCurrentUploadedTotal()) : Infinity;
                if (remainingBefore <= 0) {
                    alert(window.UploadPortalTranslations.noSpaceForTemplates);
                    input.value = '';
                    return;
                }

                try {
                    document.querySelectorAll('.upload-error').forEach(el => el.remove());
                } catch (e) {
                    /* ignore */
                }

                const textSpan = uploadFileBtn.querySelector('span');
                const originalText = textSpan ? textSpan.textContent : uploadFileBtn.textContent;

                if (textSpan) textSpan.textContent = window.UploadPortalTranslations.fileUploading;
                uploadFileBtn.classList.add('uploading');
                uploadFileBtn.disabled = true;

                try {
                    const formData = new FormData();
                    console.log(files.length);
                    Array.from(files).forEach(file => {
                        formData.append('file[]', file);
                    });

                    const resp = await fetch('https://kartent-app.monkeydo.it/file-validate', {
                        method: 'POST',
                        body: formData
                    });

                    const contentType = resp.headers.get('content-type') || '';
                    let result;
                    if (contentType.includes('application/json')) {
                        result = await resp.json();
                    } else {
                        result = await resp.text();
                    }

                    console.log(result);

                    // Check for validation error structure: object with "message" and "errors" properties
                    if (result && typeof result === 'object' && result.message === "Validation failed" && result.errors) {
                        // Check if any error is the file size error
                        let hasFileSizeError = false;
                        let otherErrors = [];
                        
                        for (const [fileKey, messages] of Object.entries(result.errors)) {
                            if (Array.isArray(messages) && messages.length > 0) {
                                for (const msg of messages) {
                                    if (msg === "Each file cannot be larger than 50 MB.") {
                                        hasFileSizeError = true;
                                    } else {
                                        const fileIndex = fileKey.match(/\d+/)?.[0];
                                        const fileLabel = fileIndex !== undefined ? `Bestand ${parseInt(fileIndex) + 1}` : fileKey;
                                        otherErrors.push(`${fileLabel}: ${msg}`);
                                    }
                                }
                            }
                        }
                        
                        // Show file size error with custom message
                        if (hasFileSizeError) {
                            renderUploadErrorSimple(uploadList, 'One or more files in your upload are larger than the maximum allowed format of 50 MB.');
                            input.value = '';
                            return;
                        }
                        
                        // Show other errors with alert
                        if (otherErrors.length > 0) {
                            const errorText = otherErrors.join('\n');
                            alert(`Validation failed:\n${errorText}`);
                            input.value = '';
                            return;
                        }
                    }

                    const responseNotEmpty = (res) => {
                        if (!res) return false;
                        if (Array.isArray(res)) return res.length > 0;
                        if (res.files && Array.isArray(res.files)) return res.files.length > 0;
                        if (typeof res === 'object') return Object.keys(res).length > 0;
                        if (typeof res === 'string') return res.trim().length > 0;
                        return true;
                    };

                    if (responseNotEmpty(result)) {


                        // Build an array of { key, data, file } so we can associate each
                        // validation result with the correct File object from the input.
                        const fileList = Array.from(files || []);
                        let filesArray = Object.keys(result).map((key, idx) => ({
                            key,
                            data: result[key],
                            // Prefer positional match, fallback to matching by filename
                            file: fileList[idx] || fileList.find(f => {
                                const maybeName = (result[key]?.result?.filename || result[key]?.filename || '').toString().trim();
                                return maybeName && f && f.name === maybeName;
                            }) || null
                        }));

                        console.log(filesArray.length);

                        // Enforce remaining capacity: only process up to the available slots
                        const remainingSlots = (typeof maxDesigns !== 'undefined' && maxDesigns !== null) ? Math.max(0, Number(maxDesigns) - getCurrentUploadedTotal()) : Infinity;
                        if (remainingSlots <= 0) {
                            alert('There is no space left to upload additional templates.');
                            input.value = '';
                            return;
                        }
                        if (filesArray.length > remainingSlots) {
                            alert(`There is only space for ${remainingSlots} file(s). The first ${remainingSlots} will be processed.`);
                            filesArray = filesArray.slice(0, remainingSlots);
                        }

                        filesArray.forEach(entry => {
                            const wrapped = { [entry.key]: entry.data };
                            const check = validateUploadResult(wrapped);
                            if (!check.ok) {
                                try {
                                    renderUploadError(uploadList, wrapped, check.reasons);
                                } catch (e) {
                                    console.error('Error rendering upload error:', e);
                                    alert('File validation failed: ' + check.reasons.join(', '));
                                }
                            } else {
                                const newNumber = amount + 1;
                                const candidateFile = entry.file || null;
                                const inferredFileName = check.data && check.data.filename ? check.data.filename : '';
                                const actualFileName = (inferredFileName && inferredFileName.trim()) ? inferredFileName : (candidateFile ? candidateFile.name : '');

                                const newDiv = addTemplate(newNumber, actualFileName, 1);

                                newDiv.dataset.orderId = orderId;
                                newDiv.dataset.productId = product.id;
                                newDiv.dataset.templateNumber = newNumber;

                                const fileResult = entry.data || {};

                                const folderKey =
                                    fileResult.folder_key ??
                                    fileResult.result?.folder_key ??
                                    '';

                                const folderPage =
                                    fileResult.page ??
                                    fileResult.result?.page ??
                                    '';

                                newDiv.dataset.templateFolderKey = folderKey || '';
                                newDiv.dataset.templatePage = folderPage || '';

                                if (candidateFile) {
                                    templateFilesMap.set(newDiv, candidateFile);
                                    // Genereer thumbnail voor de geüploade PDF (asynchroon)
                                    try { generatePdfThumbnail(candidateFile, newDiv).catch(()=>{}); } catch(e) { /* ignore */ }
                                }

                                saveTemplate(orderId, product.id, newNumber, actualFileName, 1, candidateFile, wrapped, folderKey, folderPage).catch(err => console.error('Failed saving template with validation:', err));

                                bindTemplateEvents(newDiv, newNumber, actualFileName, orderId, product, maxDesigns);

                                try {
                                    renderValidationResults(newDiv, wrapped);
                                } catch (e) {
                                    console.error('Fout bij renderen validatieresultaten:', e);
                                }

                                autoAssignTemplateWithValidation(newDiv, templateButtonUrl);

                                updateTemplateStats();
                                updateUploadedAmount();

                                amount = newNumber;
                            }
                        });
                    } else {
                        const msg = (result && result.files && result.files[0] && result.files[0].warnings && result.files[0].warnings.join('\n')) || JSON.stringify(result);
                        alert('Bestandsvalidatie niet geslaagd:\n' + msg);
                    }
                } catch (e) {
                    console.error('Validatie mislukt:', e);
                    alert('Er is iets misgegaan bij het valideren van het bestand.');
                } finally {
                    uploadFileBtn.disabled = false;
                    try {
                        if (textSpan) {
                            textSpan.textContent = originalText;
                        } else {
                            uploadFileBtn.textContent = originalText;
                        }
                    } catch (e) {
                        uploadFileBtn.textContent = originalText;
                    }
                    uploadFileBtn.classList.remove('uploading');
                }
            });
            input.click();
        });
    };

    const uploadFileButtons = Array.from(document.querySelectorAll('#upload-file'));
    uploadFileButtons.forEach(btn => {
        const cleanBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(cleanBtn, btn);
    });

    Array.from(document.querySelectorAll('#upload-file')).forEach(bindUploadFileButton);

    setupSaveButton(orderId, product.id);

    uploadWrapper.classList.add('loaded');
    loadingWrapper.classList.add('hidden');

    const sidebarProducts = document.querySelectorAll('#product-list .sidebar__product');
    if (sidebarProducts.length > 0) {
        let shouldOpenTutorial = false;

        try {
            shouldOpenTutorial = shouldOpenPortalTutorial();
        } catch (err) {
            shouldOpenTutorial = true;
        }

        if (shouldOpenTutorial) {
            requestAnimationFrame(() => {
                openPortalTutorial(getPortalTutorialResumeStepIndex(), { isAutoOpen: true });
            });
        }
    }
}

loadOrder();

function setupSaveButton(orderId, productId) {
    const saveBtn = document.getElementById("save-templates-btn");
    if (!saveBtn) return;

    let saveBtnText = saveBtn.querySelector('.add-template__text');
    if (!saveBtnText) {
        saveBtnText = document.createElement('span');
        saveBtnText.className = 'add-template__text';
        const existing = saveBtn.textContent && saveBtn.textContent.trim();
        if (existing) saveBtnText.textContent = existing;
        saveBtn.appendChild(saveBtnText);
    }

    saveBtn.onclick = async () => {
        if (saveBtn.disabled) return;

        const originalText = saveBtnText.textContent;

        try {
            saveBtnText.textContent = window.UploadPortalTranslations.saveTemplatesSending;
            saveBtn.classList.add('uploading');
            saveBtn.style.setProperty('--upload-progress', '0');
            saveBtn.disabled = true;
            saveBtn.setAttribute('aria-disabled', 'true');
        } catch (e) {
            console.warn('Could not set save button loading state:', e);
        }
        const templateDivs = document.querySelectorAll("#template-upload-list .workspace__template");
        const templatesData = [];


        // Verzamel alle template data
        for (let index = 0; index < templateDivs.length; index++) {
            const div = templateDivs[index];
            const fileNameEl = div.querySelector(".template__filename");
            const qtyInput = div.querySelector(".qty-input");
            const selectEl = div.querySelector(".template-select");
            


            if (fileNameEl && qtyInput) {
                // Haal het bestand uit IndexedDB (of WeakMap als fallback)
                let file = templateFilesMap.get(div);
                
                if (!file) {
                    file = await getTemplateBlob(orderId, productId, index + 1);
                }

                const repairedPdfFileId = div.dataset.repairedPdfFileId || '';

                if (file) {
                    templatesData.push({
                        templateNumber: index + 1,
                        fileName: fileNameEl.textContent.trim(),
                        quantity: Number(qtyInput.value),
                        selectedTemplate: selectEl ? selectEl.value : '',
                        file: file,
                        element: div,
                        folder_key: div.dataset.templateFolderKey || '',
                        page: div.dataset.templatePage || '',
                        repairedPdfFileId: repairedPdfFileId
                    });
                } else if (div.dataset.templateFolderKey && div.dataset.templatePage) {
                    templatesData.push({
                        templateNumber: index + 1,
                        fileName: fileNameEl.textContent.trim(),
                        quantity: Number(qtyInput.value),
                        selectedTemplate: selectEl ? selectEl.value : '',
                        file: null,
                        element: div,
                        folder_key: div.dataset.templateFolderKey || '',
                        page: div.dataset.templatePage || '',
                        repairedPdfFileId: repairedPdfFileId
                    });
                } else if (repairedPdfFileId) {
                    templatesData.push({
                        templateNumber: index + 1,
                        fileName: fileNameEl.textContent.trim(),
                        quantity: Number(qtyInput.value),
                        selectedTemplate: selectEl ? selectEl.value : '',
                        file: null,
                        element: div,
                        folder_key: '',
                        page: '',
                        repairedPdfFileId: repairedPdfFileId
                    });
                } else {
                console.log(`${window.UploadPortalTranslations.templateNumberNotFound.replace('{number}', index + 1)}}`);
                }
            }
        }

        if (templatesData.length === 0) {
            alert('No templates with files to save.');
            try {
                saveBtn.disabled = false;
                saveBtn.classList.remove('uploading');
                if (saveBtnText) saveBtnText.textContent = originalText;
                saveBtn.removeAttribute('aria-disabled');
            } catch (e) {
                /* ignore */
            }
            updateUploadedAmount();
            return;
        }

        console.log('Start uploading templates...');

        console.log(templatesData);

       try {
            console.log('Start bulk upload...');

            const uploadedTemplates = await uploadTemplatesBulk(
                templatesData,
                orderId,
                productId,
                (percent) => {
                    saveBtn.style.setProperty('--upload-progress', String(percent / 100));
                    if (percent >= 100) {
                        saveBtn.classList.add('processing');
                        saveBtnText.textContent = window.UploadPortalTranslations.saveTemplatesProcessing;
                    } else {
                        saveBtnText.textContent = window.UploadPortalTranslations.saveTemplatesUploadProgress.replace('{percent}', percent);
                    }
                }
            );

            console.log('All templates uploaded at once:', uploadedTemplates);

            console.log('sentProducts orderId', orderId, 'productId', productId);

            await markProductSent(orderId, productId);

            location.reload();

        } catch (error) {
            console.error("Upload Error:", error);
            alert("An error occurred while uploading: " + error.message);
        } finally {
            try {
                saveBtn.disabled = false;
                saveBtn.classList.remove('uploading', 'processing');
                saveBtn.style.removeProperty('--upload-progress');
                if (saveBtnText) saveBtnText.textContent = originalText;
                saveBtn.removeAttribute('aria-disabled');
            } catch (e) {
                /* ignore */
            }
        }

    };
}

function uploadWithProgress(url, formData, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        let lastLoggedPercent = null;
        const logInterval = setInterval(() => {
            console.log(`Upload progress: ${lastLoggedPercent ?? 0}%`);
        }, 200);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percent = Math.round((e.loaded / e.total) * 100);
                lastLoggedPercent = percent;
                onProgress(percent);
            }
        });
        xhr.onload = () => {
            clearInterval(logInterval);
            if (xhr.status >= 200 && xhr.status < 300) {
                const contentType = xhr.getResponseHeader('content-type') || '';
                if (contentType.includes('application/json')) {
                    try {
                        resolve(JSON.parse(xhr.responseText));
                    } catch (e) {
                        reject(new Error('Invalid JSON response'));
                    }
                } else {
                    resolve(xhr.responseText);
                }
            } else {
                reject(new Error(xhr.statusText || 'Bulk upload mislukt'));
            }
        };
        xhr.onerror = () => {
            clearInterval(logInterval);
            reject(new Error('Network error'));
        };
        xhr.send(formData);
    });
}

async function uploadTemplatesBulk(templates, orderId, productId, onProgress) {
    const formData = new FormData();
    const meta = [];

    formData.append("orderId", orderId);
    formData.append("lineItemId", productId);

    templates.forEach(tpl => {
        const tplLabel = tpl.selectedTemplate
            ? extractTemplateNameFromUrl(tpl.selectedTemplate)
            : `Template${tpl.templateNumber}`;


        const parts = tplLabel.replace(/\.pdf$/, '').split('-');
        const offset = !isNaN(parseInt(parts[0])) ? 1 : 0;
        const tplName = parts[offset] || '';
        const material = parts[offset + 1] || '';
        const thickness = parts[offset + 2] || '';
        
        const tplNameShort = tplName;

        const orderIdDigits = String(orderId).replace(/\D/g, '');
        const customFileName = `${orderId}-${tpl.quantity}-${tplNameShort}_${tpl.templateNumber}${orderIdDigits}-${material}-${thickness}.pdf`;

        console.log(tpl);

        if (tpl.repairedPdfFileId) {
            // Use Canva repaired_pdf file_id as location
            const fullData = tpl.repairedPdfFileId + ',' + customFileName;
            console.log('Using Canva repaired_pdf location:', fullData);
            formData.append("location[]", fullData);
        } else if (tpl.page && tpl.folder_key) {
            const fullData = tpl.folder_key + '/page_' + tpl.page + ',' + customFileName;
            console.log('fulldata', fullData);
            formData.append("location[]", fullData);
        } else {
            console.log('filleee');
            formData.append("file[]", tpl.file, customFileName);
        }

        console.log('customFileName', customFileName);

        meta.push({
            templateNumber: tpl.templateNumber,
            customFileName
        });
    });

    

    const result = await uploadWithProgress(
        "https://kartent-app.monkeydo.it/upload-file",
        formData,
        (percent) => {
            if (typeof onProgress === 'function') onProgress(percent);
        }
    );

    if (typeof result === 'string') {
        console.warn('Response is geen JSON, ontvang:', result);
    }
    console.log("Bulk upload response:", result);
    // verwacht array of object met bestandsnamen

    return meta.map((m, i) => ({
        ...m,
        fileUrl:
            "https://cdn.shopify.com/s/files/1/0840/3759/9498/files/" +
            (Array.isArray(result) ? result[i] : result[m.customFileName])
    }));
}

function bindTemplateEvents(templateDiv, number, fileName, orderId, product, maxDesigns) {
    const qtyInput = templateDiv.querySelector(".qty-input");
    const minusBtn = templateDiv.querySelector(".minus");
    const plusBtn = templateDiv.querySelector(".plus");
    const selectEl = templateDiv.querySelector(".template-select");

    function getAssignedForTemplate(templateUrl, excludeDiv = null) {
        if (!templateUrl) return 0;
        return Array.from(document.querySelectorAll('.workspace__template')).reduce((sum, row) => {
            if (excludeDiv && row === excludeDiv) return sum;
            const sel = row.querySelector('.template-select');
            const qty = row.querySelector('.qty-input');
            if (sel && sel.value === templateUrl && qty) {
                return sum + Number(qty.value);
            }
            return sum;
        }, 0);
    }

    if (selectEl) {
        selectEl.addEventListener("change", async (e) => {
            const newVal = e.target.value;
            const currentQty = qtyInput ? Number(qtyInput.value) : 1;

            const qtyWrap = templateDiv.querySelector('.template__quantity');
            if (qtyWrap) {
                if (newVal) qtyWrap.classList.remove('invisible');
                else qtyWrap.classList.add('invisible');
            }

            const tplAmount = extractTemplateAmount(extractTemplateNameFromUrl(newVal));
            const perTemplateMax = getProductQuantity(product) * tplAmount;

            const alreadyAssigned = getAssignedForTemplate(newVal, templateDiv);
            if (newVal && (alreadyAssigned + currentQty) > perTemplateMax) {
                console.log(`Kan niet selecteren: maximaal ${perTemplateMax} items per template toegestaan (reeds ${alreadyAssigned}).`)

                e.target.value = '';
                templateDiv.dataset.selectedTemplate = '';
                if (qtyWrap) qtyWrap.classList.add('invisible');
                updateTemplateStats();
                updateUploadedAmount();
                return;
            }

            try {
                const el = e.target.closest('div.workspace__template.template');
                const file = templateFilesMap.get(el);

                const folderKey = el.dataset.templateFolderKey;
                const page = el.dataset.templatePage;

                let fullData = false;

                if (folderKey && page) {
                    fullData = folderKey + '/page_' + page + '.pdf';
                }
                
                if (!(file instanceof File) && !fullData) {
                    console.error('Main file is not a File');
                }
                
                const formData = new FormData();
                if (fullData) {
                    formData.append("location[]", fullData);
                } else {
                    formData.append('file', file);
                }


                if (!newVal || (!file && !fullData)) {
                    try { saveTemplateSelection(orderId, product.id, number, '').catch(()=>{}); } catch(e){console.log(e)}
                    return;
                }

                el.classList.add('loading-template');

                const response = await fetch(newVal);
                const blob = await response.blob();

                const templateFile = new File(
                [blob],
                'template.pdf',
                { type: 'application/pdf' }
                );

                if (!(templateFile instanceof File)) {
                    console.error('Template file is not a File');
                }

                formData.append('template', templateFile);

                const modeCheckbox = getModeCheckbox();
                if (modeCheckbox && modeCheckbox.checked) {
                    formData.append('mode', modeCheckbox.value || 'canva');
                }

                const resp = await fetch('https://kartent-app.monkeydo.it/file-template-validate', {
                    method: 'POST',
                    body: formData
                });

                const contentType = resp.headers.get('content-type') || '';
                let result;
                if (contentType.includes('application/json')) {
                    result = await resp.json();
                } else {
                    result = await resp.text();
                }

                console.log('result', result.result.result);

                // Store Canva repaired_pdf file_id if present, or use the base64 content if that's what was returned instead
                let repairedPdfFileId = '';
                if (result && result.result && result.result.result && result.result.result.files && result.result.result.files.repaired_pdf) {
                    const repairedPdf = result.result.result.files.repaired_pdf;
                    if (repairedPdf && typeof repairedPdf === 'object' && repairedPdf.file_id) {
                        repairedPdfFileId = repairedPdf.file_id;
                        el.dataset.repairedPdfFileId = repairedPdfFileId;
                        console.log('Stored Canva repaired_pdf file_id:', repairedPdfFileId);
                    } else {
                        const repairedPdfBase64 = typeof repairedPdf === 'string'
                            ? repairedPdf
                            : (repairedPdf.base64 || repairedPdf.content || repairedPdf.data || repairedPdf.file_base64);
                        if (repairedPdfBase64) {
                            const repairedPdfFile = base64ToFile(repairedPdfBase64, 'repaired.pdf', 'application/pdf');
                            if (repairedPdfFile) {
                                templateFilesMap.set(el, repairedPdfFile);
                                el.dataset.repairedPdfFileId = '';
                            }
                        }
                    }
                }

                var hasErrors = renderValidationResultsTemplate(el, result);

                console.log(hasErrors);

                el.classList.remove('loading-template');

                if (hasErrors) {
                    e.target.value = '';
                    templateDiv.dataset.selectedTemplate = '';
                    if (qtyWrap) qtyWrap.classList.add('invisible');
                    updateTemplateStats();
                    updateUploadedAmount();
                    try { saveTemplateSelection(orderId, product.id, number, '').catch(()=>{}); } catch(e){}
                    return;
                }
            } catch(e) {
                const el = e.target.closest('div.workspace__template.template');
                if (el) el.classList.remove('loading-template');
                console.error('Validation failed:', e);
                alert('Something went wrong when validating the file.');
            }
            

            templateDiv.dataset.selectedTemplate = newVal;
            
            // Save repairedPdfFileId (or the base64-decoded repaired file) to IndexedDB if it exists
            const repairedPdfFileId = templateDiv.dataset.repairedPdfFileId || '';
            const repairedPdfFile = templateFilesMap.get(templateDiv);
            if (repairedPdfFileId || repairedPdfFile) {
                try {
                    saveTemplate(orderId, product.id, number, fileName, 1, repairedPdfFile || null, null, '', null, repairedPdfFileId)
                        .catch(()=>{});
                } catch(e){console.log(e)}
            }
            
            try { saveTemplateSelection(orderId, product.id, number, newVal).catch(()=>{}); } catch(e){}
            updateTemplateStats();
            updateUploadedAmount();
        });
    }

    if (minusBtn) {
        minusBtn.addEventListener("click", () => {
            if (Number(qtyInput.value) > 1) {
                qtyInput.value = Number(qtyInput.value) - 1;
                saveTemplate(orderId, product.id, number, fileName, Number(qtyInput.value));
                updateTemplateStats();
                updateUploadedAmount();
            }
        });
    }

    if (plusBtn) {
        plusBtn.addEventListener("click", () => {
            const selVal = selectEl ? selectEl.value : '';
            if (!selVal) {
                alert('Select a template first before increasing the quantity.');
                return;
            }

            const tplAmount = extractTemplateAmount(extractTemplateNameFromUrl(selVal));
            const perTemplateMax = getProductQuantity(product) * tplAmount;
            const assigned = getAssignedForTemplate(selVal);

            if (assigned >= perTemplateMax) {
                console.log(`Cannot increase: max ${perTemplateMax} items per template allowed (${assigned} already assigned).`);
                return;
            }

            qtyInput.value = Number(qtyInput.value) + 1;
            saveTemplate(orderId, product.id, number, fileName, Number(qtyInput.value));
            updateTemplateStats();
            updateUploadedAmount();
        });
    }

    if (qtyInput) {
        const clampQtyInput = (snapInvalidToMin) => {
            const raw = qtyInput.value;
            let val = parseInt(raw, 10);

            const selVal = selectEl ? selectEl.value : '';
            let maxAllowed = Infinity;
            if (selVal) {
                const tplAmount = extractTemplateAmount(extractTemplateNameFromUrl(selVal));
                const perTemplateMax = getProductQuantity(product) * tplAmount;
                const otherAssigned = getAssignedForTemplate(selVal, templateDiv);
                maxAllowed = Math.max(1, perTemplateMax - otherAssigned);
            }

            if (isNaN(val)) {
                if (!snapInvalidToMin) return;
                val = 1;
            }
            if (val < 1) val = 1;
            if (val > maxAllowed) val = maxAllowed;

            if (String(val) !== raw) qtyInput.value = val;

            saveTemplate(orderId, product.id, number, fileName, val);
            updateTemplateStats();
            updateUploadedAmount();
        };

        qtyInput.addEventListener("input", () => clampQtyInput(false));
        qtyInput.addEventListener("blur", () => clampQtyInput(true));
    }
}

function updateTemplateStats() {
    Object.keys(templateStats).forEach(tpl => templateStats[tpl] = 0);

    document.querySelectorAll(".workspace__template").forEach(row => {
        const select = row.querySelector(".template-select");
        const qty = row.querySelector(".qty-input");

        if (select && select.value && qty) {
            templateStats[select.value] += Number(qty.value);
        }
    });

    document.querySelectorAll(".workspace__template_stat").forEach(statRow => {
        const tpl = statRow.dataset.template;
        const amountEl = statRow.querySelector(".template_amount");
        const isFixedCounter = statRow.dataset.fixedCounter === 'true';

        if (!amountEl) return;

        console.log(isFixedCounter);

        if (isFixedCounter) {
            // amountEl.textContent = '10/10';
            statRow.classList.add('is-complete');
            statRow.classList.remove('is-incomplete');
            return;
        }

        const max = amountEl.textContent.split("/")[1];
        const assigned = tpl && templateStats[tpl] ? Number(templateStats[tpl]) : 0;
        const required = max ? Number(max) : 0;

        amountEl.textContent = `${assigned}/${max}`;

        if (required > 0 && assigned >= required) {
            statRow.classList.add('is-complete');
            statRow.classList.remove('is-incomplete');
        } else {
            statRow.classList.remove('is-complete');
            statRow.classList.add('is-incomplete');
        }
    });

    try {
        refreshSelectOptions();
    } catch (e) {
        console.error('Error refreshing select options:', e);
    }
}

function refreshSelectOptions() {
    const requiredMap = {};
    document.querySelectorAll('.workspace__template_stat').forEach(statRow => {
        if (statRow.dataset.fixedCounter === 'true') return;
        const tpl = statRow.dataset.template;
        const amountEl = statRow.querySelector('.template_amount');
        if (!tpl || !amountEl) return;
        const [, required] = amountEl.textContent.split('/').map(Number);
        requiredMap[tpl] = required || 0;
    });

    document.querySelectorAll('.template-select').forEach(select => {
        Array.from(select.options).forEach(opt => {
            const tplUrl = opt.value;
            if (!tplUrl) return opt.disabled = false;

            const required = requiredMap[tplUrl] || 0;
            const assigned = Number(templateStats[tplUrl] || 0);

            if (required > 0 && assigned >= required && select.value !== tplUrl) {
                opt.disabled = true;
            } else {
                opt.disabled = false;
            }
        });
    });
}

window.removeTemplate = function(button) {
    const templateDiv = button.closest(".workspace__template");
    if (!templateDiv) return;

    const orderId = templateDiv.dataset.orderId;
    const productId = templateDiv.dataset.productId;
    const tplNumber = templateDiv.dataset.templateNumber;

    try { templateFilesMap.delete(templateDiv); } catch (e) { /* ignore */ }

    if (orderId && productId && tplNumber) {
        deleteTemplateFromDB(orderId, productId, tplNumber).then(() => {
            templateDiv.remove();
            updateUploadedAmount();
            updateTemplateStats();
        }).catch((err) => {
            console.error('Error removing template from DB:', err);
            templateDiv.remove();
            updateUploadedAmount();
            updateTemplateStats();
        });
    } else {
        templateDiv.remove();
        updateUploadedAmount();
        updateTemplateStats();
    }
}

function updateUploadedAmount() {
    const allQtyInputs = document.querySelectorAll(".template__quantity .qty-input");
    const total = Array.from(allQtyInputs).reduce((sum, input) => sum + Number(input.value), 0);

    if (addBtn) addBtn.style.display = 'none';

    let allFilled = true;
    const statRows = document.querySelectorAll('.workspace__template_stat');
    statRows.forEach(statRow => {
        if (statRow.dataset.fixedCounter === 'true') return;
        const tpl = statRow.dataset.template;
        const amountEl = statRow.querySelector('.template_amount');
        if (!amountEl) {
            allFilled = false;
            return;
        }

        const parts = amountEl.textContent.split('/');
        const required = parts[1] ? Number(parts[1]) : 0;
        const assigned = tpl && templateStats[tpl] ? Number(templateStats[tpl]) : 0;

        if (assigned < required) allFilled = false;
    });

    if (saveBtn) {
        saveBtn.style.display = 'flex';
        saveBtn.classList.toggle('disabled', !allFilled);
        saveBtn.disabled = !allFilled;
        saveBtn.setAttribute('aria-disabled', String(!allFilled));
    }


    const uploadFileButtons = document.querySelectorAll('#upload-file');
    const shouldDisableUploadButtons = (
        typeof maxDesigns !== 'undefined' &&
        maxDesigns !== null &&
        total >= Number(maxDesigns)
    );

    uploadFileButtons.forEach(uploadFileBtn => {
        if (shouldDisableUploadButtons) {
            uploadFileBtn.disabled = true;
            uploadFileBtn.classList.add('is-disabled');
            uploadFileBtn.setAttribute('aria-disabled', 'true');
            uploadFileBtn.setAttribute('tabindex', '-1');
        } else {
            uploadFileBtn.disabled = false;
            uploadFileBtn.classList.remove('is-disabled');
            uploadFileBtn.removeAttribute('aria-disabled');
            uploadFileBtn.removeAttribute('tabindex');
        }
    });
}


function getCurrentUploadedTotal() {
    const allQtyInputs = document.querySelectorAll(".template__quantity .qty-input");
    return Array.from(allQtyInputs).reduce((sum, input) => sum + Number(input.value), 0);
}

function getValidationText(element) {
    if (!element) return '';
    if (typeof element === 'string') return element;

    const lang = (normalizedShopLang || 'nl').toLowerCase();
    return (element.translations && element.translations[lang]) || element.text || '';
}

function renderValidationResults(templateDiv, validation) {
    if (!templateDiv || !validation) return;

    let container = templateDiv.querySelector('.template__validation-results');
    if (!container) {
        container = document.createElement('div');
        container.className = 'template__validation-results';
        const info = templateDiv.querySelector('.template__info');
        if (info) info.appendChild(container);
        else templateDiv.appendChild(container);
    }

    let data = validation;
    if (typeof validation === 'object' && !Array.isArray(validation)) {
        const keys = Object.keys(validation);
        if (keys.length === 1 && validation[keys[0]] && typeof validation[keys[0]] === 'object') {
            data = validation[keys[0]].result || validation[keys[0]];
        } else if (validation.result && typeof validation.result === 'object') {
            data = validation.result;
        }
    }

    const warnings = Array.isArray(data.result.level.warnings) ? data.result.level.warnings : [];

    let warningParts = [];
    warnings.forEach(element => {
        warningParts.push('<div class="template__warning">'+ getValidationText(element) +'</div>');
    });

    const templateEl = container.closest('.template');

    if (warningParts !== null && warningParts.length > 0) {
        let warningsContainer = templateEl.querySelector('.template__warnings');
        if (warningsContainer) {
            warningsContainer.insertAdjacentHTML('beforeend', warningParts.join('\n'));
        } else {
            templateEl.insertAdjacentHTML(
                'beforeend',
                `<div class="template__warnings">${warningParts.join('\n')}</div>`
            );
        }
    }

    // container.innerHTML = parts.join('\n');
}

function renderValidationResultsTemplate(templateDiv, validation) {
    if (!templateDiv || !validation) return;

    console.log(validation);

    try {
        templateDiv.querySelectorAll('.template_warning_template, .template_error_template').forEach(el => el.remove());
    } catch (e) {
        /* ignore */
    }

    let container = templateDiv.querySelector('.template__validation-results');
    if (!container) {
        container = document.createElement('div');
        container.className = 'template__validation-results';
        const info = templateDiv.querySelector('.template__info');
        if (info) info.appendChild(container);
        else templateDiv.appendChild(container);
    }

    let data = validation;
    if (typeof validation === 'object' && !Array.isArray(validation)) {
        if (validation.result && typeof validation.result === 'object') {
            data = validation.result;
        }
    }

    const warnings = Array.isArray(data.result.level.warnings) ? data.result.level.warnings : [];
    const errors = Array.isArray(data.result.level.errors) ? data.result.level.errors : [];
    const files = (data.result && data.result.files && typeof data.result.files === 'object') ? data.result.files : null;
    const hasErrorPreview = files && files.error_preview && typeof files.error_preview === 'string' && files.error_preview.trim() !== '';

    let warningParts = [];
    warnings.forEach(element => {
        let html = '<div class="template__warning template_warning_template">'+ getValidationText(element);
            if (hasErrorPreview) {
                html += '<div class="template__error-preview"><div class="template__error--subtitle">Error file</div><img src="data:image/png;base64,'+ files.error_preview +'"></div>';
            }
        html += '</div>';

        warningParts.push(html);
    });

    const templateEl = container.closest('.template');

    if (warningParts !== null && warningParts.length > 0) {
        let warningsContainer = templateEl.querySelector('.template__warnings');
        if (warningsContainer) {
            warningsContainer.insertAdjacentHTML('beforeend', warningParts.join('\n'));
        } else {
            templateEl.insertAdjacentHTML(
                'beforeend',
                `<div class="template__warnings">${warningParts.join('\n')}</div>`
            );
        }
    }

    let errorParts = [];
    errors.forEach(element => {
        let html = '<div class="template__error template_error_template">'+ getValidationText(element);
            if (hasErrorPreview) {
                html += '<div class="template__error-preview"><div class="template__error--subtitle">Error file</div><img src="data:image/png;base64,'+ files.error_preview +'"></div>';
            }
        html += '</div>';

        errorParts.push(html);
    });

    if (errorParts !== null && errorParts.length > 0) {
        let errorsContainer = templateEl.querySelector('.template__errors');
        if (errorsContainer) {
            errorsContainer.insertAdjacentHTML('beforeend', errorParts.join('\n'));
        } else {
            templateEl.insertAdjacentHTML(
                'beforeend',
                `<div class="template__errors">${errorParts.join('\n')}</div>`
            );
        }

        return true;
    }

    // container.innerHTML = parts.join('\n');
}

function validateUploadResult(validation) {
    if (!validation) return { ok: false, reasons: ['No validation result'], data: null };

    let data = validation;
    if (typeof validation === 'object' && !Array.isArray(validation)) {
        const keys = Object.keys(validation);
        if (keys.length === 1 && validation[keys[0]] && typeof validation[keys[0]] === 'object') {
            data = validation[keys[0]].result || validation[keys[0]];
        } else if (validation.result && typeof validation.result === 'object') {
            data = validation.result;
        }
    }



    const reasons = Array.isArray(data.result.level.errors) ? data.result.level.errors : [];

    // Check page count === 1
    // if (typeof data.page_count !== 'undefined') {
    //     if (Number(data.page_count) !== 1) reasons.push(`page_count is ${data.page_count}`);
    // } else {
    //     reasons.push('page_count missing');
    // }

    // Check that cut contour layer exists / is true
    // if (typeof data.has_cut_contour_layer !== 'undefined') {
    //     if (!data.has_cut_contour_layer) reasons.push('no cut contour layer detected');
    // } else {
    //     reasons.push('has_cut_contour_layer missing');
    // }

    // You can add more checks here (color mode, image resolution, etc.)

    return { ok: reasons.length === 0, reasons, data };
}

function renderUploadError(container, validation, reasons = []) {
    if (!container) return null;

    let data = validation;
    if (typeof validation === 'object' && !Array.isArray(validation)) {
        const keys = Object.keys(validation);
        if (keys.length === 1 && validation[keys[0]] && typeof validation[keys[0]] === 'object') {
            data = validation[keys[0]].result || validation[keys[0]];
        } else if (validation.result && typeof validation.result === 'object') {
            data = validation.result;
        }
    }

    const pageCount = (typeof data.page_count !== 'undefined') ? data.page_count : 'Unknown';
    const warnings = Array.isArray(data.result.level.warnings) ? data.warnings : [];
    const docTitle = (typeof data.filename === 'string') ? data.filename : 'Unknown';

    // Alleen tonen als page_count > 1
    const showPages = (typeof pageCount === 'number' && pageCount > 1);

    const errorDiv = document.createElement('div');
    errorDiv.className = 'upload-error';
    errorDiv.innerHTML = `
        <div class="upload-error__wrap">
            <div class="upload-error__title">
                <div class="upload-error__heading">Document rejected</div>
                <div class="upload-error__filename">${docTitle}</div>
            </div>
            
            <div class="upload-error__body">
                ${showPages ? `<div>Pages: <strong>${pageCount}</strong></div>` : ''}
                
                <div class="upload-error__heading">Reason:</div>
                <ul class="upload-error__reasons">
                    ${reasons.map(r => 
                        `
                        <li>
                            <span>${getValidationText(r)}</span>
                            ${(r.help) ? `<a href="javascript:;" class="upload-error__help-link" style="text-decoration:underline; color: #c93232;" onclick="openHelpLightbox('https:\/\/cdn.shopify.com\/videos\/c\/o\/v\/2488daf855f24e708b713aab22100306.mp4')">Meer informatie</a>` : ''}
                        </li>
                    `).join('')}
                </ul>
            </div>
        </div>

        <div class="upload-error__actions">
            <button class="btn btn--small upload-error__dismiss">${window.UploadPortalTranslations.closeButton}</button>
        </div>
        
    `;

    container.insertBefore(errorDiv, container.firstChild);

    const btn = errorDiv.querySelector('.upload-error__dismiss');
    if (btn) btn.addEventListener('click', () => errorDiv.remove());

    return errorDiv;
}

function renderUploadErrorSimple(container, reason) {
    if (!container) return null;

    const errorDiv = document.createElement('div');
    errorDiv.className = 'upload-error';
    errorDiv.innerHTML = `
        <div class="upload-error__wrap">
            <div class="upload-error__title">
                <div class="upload-error__heading">Document rejected</div>
            </div>
            
            <div class="upload-error__body">
                <div class="upload-error__heading">Reason:</div>
                <ul class="upload-error__reasons">
                    <li>
                        <span>${reason}</span>
                        <a href="javascript:;" class="upload-error__help-link hidden" style="text-decoration:underline; color: #c93232;" onclick="openHelpLightbox('https:\/\/cdn.shopify.com\/videos\/c\/o\/v\/2488daf855f24e708b713aab22100306.mp4')">Meer informatie</a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="upload-error__actions">
            <button class="btn btn--small upload-error__dismiss">${window.UploadPortalTranslations.closeButton}</button>
        </div>
        
    `;

    container.insertBefore(errorDiv, container.firstChild);

    const btn = errorDiv.querySelector('.upload-error__dismiss');
    if (btn) btn.addEventListener('click', () => errorDiv.remove());

    return errorDiv;
}

function isValidUrl(string) {
    if (!string || typeof string !== 'string') return false;
    try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
        return false;
    }
}

window.openHelpLightbox = function(url) {
    const isVideo = /\.(mp4|webm|ogg)$/i.test(url) || 
                    url.includes('youtube.com') || 
                    url.includes('vimeo.com');
    
    const pswpElement = document.querySelectorAll('.pswp')[0];
    
    let items = [];
    
    if (isVideo) {
        let videoHtml = '';
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
            videoHtml = `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`;
        } else if (url.includes('vimeo.com')) {
            const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
            videoHtml = `<iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
        } else {
            videoHtml = `<video controls><source src="${url}" type="video/mp4"></video>`;
        }
        
        items.push({
            html: `<div class="pswp__video-wrap">${videoHtml}</div>`,
            w: 1920,
            h: 1080
        });
    } else {
        // For images
        items.push({
            src: url,
            w: 1920,
            h: 1080
        });
    }

    console.log(items);
    
    const options = {
        index: 0,
        closeOnScroll: false,
        history: false,
        shareEl: false,
        counterEl: false,
        bgOpacity: 0.5,
        tapToClose: true,
        clickToCloseNonZoomable: true
    };
    
    const gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    gallery.init();
}

// --- PDF.js helper: dynamically load and render first page as thumbnail ---
function ensurePdfJsLoaded() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
        script.onload = () => {
            try {
                if (window.pdfjsLib) {
                    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
                    resolve(window.pdfjsLib);
                } else {
                    reject(new Error('pdfjs loaded but global not found'));
                }
            } catch (e) {
                reject(e);
            }
        };
        script.onerror = () => reject(new Error('Failed to load pdf.js script'));
        document.head.appendChild(script);
    });
}

async function generatePdfThumbnail(fileOrBlob, templateDiv, maxWidth = 800) {
    if (!fileOrBlob || !templateDiv) return;
    try {
        const pdfjsLib = await ensurePdfJsLoaded();

        let arrayBuffer;
        if (typeof fileOrBlob.arrayBuffer === 'function') {
            arrayBuffer = await fileOrBlob.arrayBuffer();
        } else {
            arrayBuffer = await new Response(fileOrBlob).arrayBuffer();
        }

        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });

        const scale = Math.min(1, maxWidth / viewport.width);
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(scaledViewport.width);
        canvas.height = Math.floor(scaledViewport.height);
        const ctx = canvas.getContext('2d');

        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;

        const dataUrl = canvas.toDataURL('image/png');

        const info = templateDiv.querySelector('.template__info');
        if (info) {
            const existing = info.querySelector('.template__thumb');
            if (existing) {
                existing.src = dataUrl;
            } else {
                const img = document.createElement('img');
                img.className = 'template__thumb';
                img.src = dataUrl;
                img.alt = templateDiv.querySelector('.template__filename')?.textContent?.trim() || 'PDF thumbnail';
                img.width = Math.min(maxWidth, canvas.width);
                info.insertBefore(img, info.firstChild);
                templateDiv.querySelector('.template__thumb-placeholder')?.remove();
            }
        }
    } catch (e) {
        console.warn('generatePdfThumbnail error:', e);
    }
}

async function generatePdfThumbnailFromUrl(url, targetEl, maxWidth = 300) {
    if (!url || !targetEl) return;
    try {
        // fetch de pdf als blob
        const resp = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
        if (!resp.ok) return;
        const arrayBuffer = await resp.arrayBuffer();

        const pdfjsLib = await ensurePdfJsLoaded();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });

        const scale = Math.min(1, maxWidth / viewport.width);
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(scaledViewport.width);
        canvas.height = Math.floor(scaledViewport.height);
        const ctx = canvas.getContext('2d');

        await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;

        const dataUrl = canvas.toDataURL('image/png');

        // Reinig doel-element en plaats thumbnail
        targetEl.innerHTML = '';
        const img = document.createElement('img');
        img.className = 'doc-preview__img';
        img.src = dataUrl;
        img.alt = 'PDF preview';
        img.style.maxWidth = Math.min(maxWidth, canvas.width) + 'px';
        img.style.width = '100%';
        img.style.height = 'auto';
        targetEl.appendChild(img);
    } catch (e) {
        // fail silently
        console.warn('generatePdfThumbnailFromUrl error:', e);
    }
}

// --- Thumbnail lightbox: show larger image when clicking a thumbnail ---
function openPdfThumbnailLightbox(src, alt = '') {
    if (!src) return;
    // If already open, replace image
    let existing = document.querySelector('.pdf-thumb-lightbox');
    if (existing) {
        const img = existing.querySelector('img');
        if (img) { img.src = src; img.alt = alt; }
        return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'pdf-thumb-lightbox';
    overlay.innerHTML = `
        <div class="pdf-thumb-lightbox__inner" role="dialog" aria-modal="true">
            <button class="pdf-thumb-lightbox__close" aria-label="${window.UploadPortalTranslations.closeButton}">
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="24px" fill="#006377"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"/></svg>
            </button>
            <img src="${src}" alt="${alt}">
        </div>
    `;

    function onKey(e) {
        if (e.key === 'Escape') closePdfThumbnailLightbox();
    }

    overlay.addEventListener('click', (ev) => {

        if (ev.target === overlay) closePdfThumbnailLightbox();
    });

    overlay.querySelector('.pdf-thumb-lightbox__close')?.addEventListener('click', () => closePdfThumbnailLightbox());

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    overlay._onKey = onKey;
}

function closePdfThumbnailLightbox() {
    const overlay = document.querySelector('.pdf-thumb-lightbox');
    if (!overlay) return;
    const onKey = overlay._onKey;
    if (onKey) window.removeEventListener('keydown', onKey);
    overlay.remove();
    document.body.style.overflow = '';
}

document.addEventListener('click', (e) => {
    try {
        const thumb = e.target.closest && e.target.closest('.template__thumb');
        if (!thumb) return;
        const src = thumb.src;
        const alt = thumb.alt || '';
        if (src) openPdfThumbnailLightbox(src, alt);
    } catch (err) {
        // swallow
    }
});

// Mobile sidebar toggle
const toggleBtn = document.getElementById('toggle-sidebar-btn');
const closeBtn  = document.getElementById('close-sidebar-btn');
const sidebar   = document.querySelector('.sidebar');
const overlay   = document.querySelector('.sidebar-overlay');

toggleBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  sidebar.classList.toggle('open');
  overlay.classList.toggle('open');
});

closeBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  closeSidebar();
});

overlay.addEventListener('click', closeSidebar);

document.addEventListener('click', (e) => {
  if (
    sidebar.classList.contains('open') &&
    !sidebar.contains(e.target) &&
    !toggleBtn.contains(e.target)
  ) {
    closeSidebar();
  }
});

function closeSidebar() {
  sidebar.classList.remove('open');
  overlay.classList.remove('open');
}

function getPortalTutorialState() {
    try {
        const rawState = window.localStorage.getItem(PORTAL_TUTORIAL_STATE_KEY);
        if (rawState) {
            const parsedState = JSON.parse(rawState);
            if (parsedState && typeof parsedState === 'object') {
                return parsedState;
            }
        }

        // Backward compatibility: old key means step 1 was already shown once.
        const hasLegacySeenFlag = window.localStorage.getItem(PORTAL_TUTORIAL_KEY);
        if (hasLegacySeenFlag) {
            return {
                completed: true,
                completedAt: Date.now(),
                source: 'legacy'
            };
        }
    } catch (err) {
        return null;
    }

    return null;
}

function savePortalTutorialState(nextState = {}) {
    try {
        const prevState = getPortalTutorialState() || {};
        const mergedState = Object.assign({}, prevState, nextState);
        window.localStorage.setItem(PORTAL_TUTORIAL_STATE_KEY, JSON.stringify(mergedState));
        if (mergedState.completed) {
            window.localStorage.setItem(PORTAL_TUTORIAL_KEY, '1');
        }
    } catch (err) {
        // ignore storage errors
    }
}

function shouldOpenPortalTutorial() {
    // Auto-show on a genuine first visit (no state yet), or if the auto-triggered
    // popup itself was still open when the page reloaded (isOpen never got cleared
    // by a close). A manually opened session (via the info icon) is not tagged as
    // an auto session, so leaving it open across a reload does not bring it back —
    // it just stays closed until reopened via the icon again.
    const currentState = getPortalTutorialState();
    if (!currentState) return true;
    return currentState.isOpen === true && currentState.isAutoSession === true;
}

function getPortalTutorialResumeStepIndex() {
    const currentState = getPortalTutorialState();
    if (!currentState || currentState.completed === true) return 0;

    const stepIndex = Number(currentState.lastSeenStepIndex);
    return Number.isFinite(stepIndex) && stepIndex >= 0 ? stepIndex : 0;
}

function resetPortalTutorialState() {
    try {
        window.localStorage.removeItem(PORTAL_TUTORIAL_STATE_KEY);
        window.localStorage.removeItem(PORTAL_TUTORIAL_KEY);
    } catch (err) {
        // ignore storage errors
    }
}

function shouldPersistPortalTutorialProgress(stepIndex = currentPortalTutorialStepIndex) {
    try {
        const hasExistingTutorialState = Boolean(window.localStorage.getItem(PORTAL_TUTORIAL_STATE_KEY));
        return hasExistingTutorialState || Number(stepIndex) > 0;
    } catch (err) {
        return Number(stepIndex) > 0;
    }
}

function getPortalTutorialTranslations() {
    const translations = window.UploadPortalTranslations || {};
    const steps = {};

    for (let index = 1; index <= 11; index += 1) {
        const stepKey = `tutorial_step_${index}`;
        steps[stepKey] = {
            title: translations[`${stepKey}_title`] || `Stap ${index}`,
            text: translations[`${stepKey}_text`] || ''
        };
    }

    return {
        stepPrefix: translations.tutorial_step || 'Stap',
        title: translations.tutorial_title || 'Upload je bestanden',
        text: translations.tutorial_text || 'Selecteer eerst het product aan de linkerkant om de juiste templates te zien.',
        nextLabel: translations.tutorial_next_button || 'Volgende',
        previousLabel: translations.tutorial_previous_button || 'Vorige',
        closeLabel: translations.tutorial_close_button || 'Sluiten',
        spTitle: translations.tutorial_sp_step_title || 'SP-template',
        spText: translations.tutorial_sp_step_text || 'Open hier de SP-template om het product verder af te ronden.',
        steps
    };
}

function getPortalTutorialSpTemplateTargetElement() {
    const candidates = Array.from(document.querySelectorAll(
        '.workspace-item[data-href], .workspace__info-file-item a[data-template-name], .download-btn[data-templateName], .edit-btn'
    ));

    const spCandidates = candidates.filter((candidate) => {
        const candidateHref = candidate.getAttribute('data-href')
            || candidate.getAttribute('href')
            || candidate.getAttribute('data-templateName')
            || candidate.getAttribute('data-template-name')
            || '';

        return isSpTemplate(extractTemplateNameFromUrl(candidateHref));
    });

    const preferred = spCandidates.find(candidate => candidate.closest('.workspace-item[data-href]')) || spCandidates[0];
    return preferred || null;
}

function getPortalTutorialSpStep() {
    const translations = getPortalTutorialTranslations();
    const spTarget = getPortalTutorialSpTemplateTargetElement();
    if (!spTarget) return null;

    return {
        id: 'step-sp',
        isSeparateSpStep: true,
        targetSelector: '.workspace-item[data-href], .workspace__info-file-item a[data-template-name], .download-btn[data-templateName], .edit-btn',
        targetFallbackSelector: '.workspace__info-file-item a[data-template-name]',
        placementDesktop: 'top',
        placementMobile: 'center',
        spotlightPadding: { desktop: 16, mobile: 10 },
        title: translations.spTitle,
        text: translations.spText
    };
}

function getPortalTutorialSteps() {
    const translations = getPortalTutorialTranslations();

    const buildStep = (stepId, selector, options = {}) => {
        const stepConfig = translations.steps[`tutorial_step_${stepId.replace('step-', '')}`] || {};
        return {
            id: stepId,
            targetSelector: selector,
            placementDesktop: options.placementDesktop || 'right',
            placementMobile: options.placementMobile || 'center',
            spotlightPadding: options.spotlightPadding || { desktop: 12, mobile: 12 },
            title: stepConfig.title || options.defaultTitle || `Stap ${stepId.replace('step-', '')}`,
            text: stepConfig.text || options.defaultText || '',
            ...(options.targetFallbackSelector ? { targetFallbackSelector: options.targetFallbackSelector } : {}),
            ...(options.spotlightMaxHeight ? { spotlightMaxHeight: options.spotlightMaxHeight } : {})
        };
    };

    const steps = [
        buildStep('step-1', '#upload-wrapper .sidebar', {
            placementDesktop: 'right',
            placementMobile: 'center',
            spotlightPadding: { desktop: 0, mobile: 0 },
            spotlightMaxHeight: 560,
            defaultTitle: translations.title,
            defaultText: translations.text
        }),
        buildStep('step-2', '#mode-indicator', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: { top: 10, right: 12, bottom: 10, left: 12 },
            defaultTitle: 'Stap 2',
            defaultText: 'Gebruik de modus-indicator om de juiste workflow te kiezen.'
        }),
        buildStep('step-3', '#instructions-download-btn', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: { top: 8, right: 12, bottom: 8, left: 12 },
            defaultTitle: 'Stap 3',
            defaultText: 'Bekijk hier de opmaakinstructies voordat je begint met uploaden.'
        }),
        buildStep('step-4', '.download-btn', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: { top: 8, right: 12, bottom: 8, left: 12 },
            defaultTitle: 'Stap 4',
            defaultText: 'Download hier het templatebestand.'
        }),
        buildStep('step-5', '.edit-btn', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: { top: 8, right: 10, bottom: 8, left: 10 },
            defaultTitle: 'Stap 5',
            defaultText: 'Open hier de editor om het bestand verder aan te passen.'
        }),
        buildStep('step-6', '#upload-file', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: 8,
            defaultTitle: 'Stap 6',
            defaultText: 'Upload hier je bestand nadat je het hebt aangepast.'
        }),
        buildStep('step-7', '.workspace__dash #upload-file ', {
            placementDesktop: 'top',
            placementMobile: 'center',
            spotlightPadding: 10,
            defaultTitle: 'Stap 7',
            defaultText: 'Kies hier de juiste template voor dit bestand.'
        }),
        buildStep('step-8', '.portal-tutorial-upload-list-placeholder__selection.template__selection', {
            placementDesktop: 'top',
            placementMobile: 'center',
            spotlightPadding: { top: 8, right: 12, bottom: 8, left: 12 },
            defaultTitle: 'Stap 8',
            defaultText: 'Pas hier het aantal aan voor deze template.',
            targetFallbackSelector: '#template-upload-list .workspace__template:first-of-type .template__selection'
        }),
        buildStep('step-9', '.portal-tutorial-upload-list-placeholder__qty.template__quantity', {
            placementDesktop: 'top',
            placementMobile: 'center',
            spotlightPadding: { top: 4, right: 10, bottom: 4, left: 10 },
            defaultTitle: 'Stap 9',
            defaultText: 'Controleer hier nog even je selectie voordat je verstuurt.',
            targetFallbackSelector: '#template-upload-list .workspace__template:first-of-type .template__quantity'
        }),
        buildStep('step-10', '#save-templates-btn', {
            placementDesktop: 'top',
            placementMobile: 'center',
            spotlightPadding: { desktop: 12, mobile: 12 },
            defaultTitle: 'Stap 10',
            defaultText: 'Klik hier om je bestanden te versturen.'
        }),
        buildStep('step-11', '#open-portal-tutorial-btn', {
            placementDesktop: 'bottom',
            placementMobile: 'center',
            spotlightPadding: { top: 8, right: 8, bottom: 8, left: 8 },
            defaultTitle: 'Stap 11',
            defaultText: 'Klik altijd op dit i-tje als je deze stappen nog eens wilt zien.'
        })
    ];

    const spStep = getPortalTutorialSpStep();
    if (!spStep) return steps;

    const insertIndex = 6;
    return [
        ...steps.slice(0, insertIndex),
        spStep,
        ...steps.slice(insertIndex)
    ];
}

function getPortalTutorialElements() {
    const tutorial = document.getElementById('portal-tutorial-overlay');
    if (!tutorial) return null;

    return {
        tutorial,
        backdrop: tutorial.querySelector('.portal-tutorial-overlay__backdrop'),
        spotlight: tutorial.querySelector('.portal-tutorial-overlay__spotlight'),
        card: tutorial.querySelector('.portal-tutorial-overlay__card'),
        stepLabel: tutorial.querySelector('[data-portal-tutorial-step]'),
        title: tutorial.querySelector('[data-portal-tutorial-title]'),
        text: tutorial.querySelector('[data-portal-tutorial-text]'),
        nextBtn: tutorial.querySelector('[data-portal-tutorial-next]'),
        prevBtn: tutorial.querySelector('[data-portal-tutorial-prev]')
    };
}

function clearPortalTutorialTargetHighlight() {
    const ui = getPortalTutorialElements();
    if (!ui || !ui.tutorial || !ui.spotlight) return;

    ui.tutorial.classList.remove('portal-tutorial-overlay--spotlight-active');
    ui.spotlight.style.opacity = '0';
    ui.spotlight.style.width = '0px';
    ui.spotlight.style.height = '0px';
}

function getPortalTutorialUploadListPlaceholder() {
    return document.querySelector(`#template-upload-list .${PORTAL_TUTORIAL_UPLOAD_LIST_PLACEHOLDER_CLASS}`);
}

function ensurePortalTutorialUploadListPlaceholder() {
    const existing = getPortalTutorialUploadListPlaceholder();
    if (existing) return existing;

    const uploadList = document.getElementById('template-upload-list');
    if (!uploadList) return null;

    const placeholder = document.createElement('article');
    placeholder.className = `${PORTAL_TUTORIAL_UPLOAD_LIST_PLACEHOLDER_CLASS}`;
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.innerHTML = `
        <div class="portal-tutorial-upload-list-placeholder__inner template__inner">
            <div class="portal-tutorial-upload-list-placeholder__info template__info">
                <div class="portal-tutorial-upload-list-placeholder__thumb template__thumb-placeholder"></div>
                <div class="portal-tutorial-upload-list-placeholder__filename template__filename">voorbeeld-bestand.pdf</div>
            </div>
            <div class="portal-tutorial-upload-list-placeholder__meta template__right">
                <div class="portal-tutorial-upload-list-placeholder__selection template__selection">
                    <select disabled>
                        <option>Template 1 - 90x50 mm</option>
                    </select>
                </div>
                <div class="portal-tutorial-upload-list-placeholder__qty template__quantity" aria-hidden="true">
                    <button class="qty-btn" type="button" disabled>−</button>
                    <input type="number" class="qty-input" value="1" min="1" readonly tabindex="-1" />
                    <button class="qty-btn" type="button" disabled>+</button>
                </div>
                <span class="portal-tutorial-upload-list-placeholder__remove template__remove" aria-hidden="true">
                    <img src="https://cdn.shopify.com/s/files/1/0840/3759/9498/files/trash-fill.svg?v=1765292249" width="18" alt="" />
                </span>
            </div>
        </div>
    `;

    uploadList.appendChild(placeholder);

    return placeholder;
}

function togglePortalTutorialUploadListPlaceholder(visible = false) {
    const uploadList = document.getElementById('template-upload-list');
    const placeholder = getPortalTutorialUploadListPlaceholder() || ensurePortalTutorialUploadListPlaceholder();
    if (!uploadList || !placeholder) return;

    const hasRealTemplates = uploadList.querySelectorAll('.workspace__template').length > 0;

    if (visible && !hasRealTemplates) {
        placeholder.classList.add('is-visible');
        return;
    }

    placeholder.classList.remove('is-visible');
}

function hasRenderableRect(element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function getPortalTutorialStepTargetElement(step) {
    if (!step) return null;

    if (step.isSeparateSpStep) {
        const spTarget = getPortalTutorialSpTemplateTargetElement();
        if (spTarget && hasRenderableRect(spTarget)) return spTarget;
    }

    const fallbackTarget = step.targetFallbackSelector ? document.querySelector(step.targetFallbackSelector) : null;
    if (hasRenderableRect(fallbackTarget)) return fallbackTarget;

    if (!step.targetSelector) return fallbackTarget || null;

    const primaryTarget = document.querySelector(step.targetSelector);
    if (hasRenderableRect(primaryTarget)) return primaryTarget;

    return primaryTarget || fallbackTarget || null;
}

function restorePortalTutorialForcedVisibility() {
    if (portalTutorialForcedVisibleEl) {
        portalTutorialForcedVisibleEl.classList.add('invisible');
        portalTutorialForcedVisibleEl = null;
    }
}

function forcePortalTutorialTargetVisible(targetElement) {
    if (targetElement !== portalTutorialForcedVisibleEl) {
        restorePortalTutorialForcedVisibility();
    }

    if (targetElement && targetElement.classList.contains('invisible')) {
        targetElement.classList.remove('invisible');
        portalTutorialForcedVisibleEl = targetElement;
    }
}

function setPortalTutorialTargetHighlight(step) {
    clearPortalTutorialTargetHighlight();
    if (!step || !step.targetSelector) {
        restorePortalTutorialForcedVisibility();
        return;
    }

    const ui = getPortalTutorialElements();
    if (!ui || !ui.tutorial || !ui.spotlight) return;

    const targetElement = getPortalTutorialStepTargetElement(step);
    if (!targetElement) {
        restorePortalTutorialForcedVisibility();
        return;
    }

    forcePortalTutorialTargetVisible(targetElement);

    const targetRect = targetElement.getBoundingClientRect();
    if (targetRect.width === 0 || targetRect.height === 0) return;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isMobile = viewportWidth <= 992;
    const spotlightPadding = getPortalTutorialSpotlightPadding(step, isMobile);
    const spotlightRadius = Number.isFinite(Number(step.spotlightRadius)) ? Number(step.spotlightRadius) : 14;

    const left = Math.max(8, targetRect.left - spotlightPadding.left);
    const top = Math.max(8, targetRect.top - spotlightPadding.top);
    const right = Math.min(viewportWidth - 8, targetRect.right + spotlightPadding.right);
    let bottom = Math.min(viewportHeight - 8, targetRect.bottom + spotlightPadding.bottom);

    // A target much taller than the viewport (e.g. a long sidebar) would otherwise
    // stretch the "hole" almost to the bottom of the screen — cap it so only a
    // reasonable top slice is highlighted.
    const maxHeight = Number(step.spotlightMaxHeight);
    if (Number.isFinite(maxHeight) && maxHeight > 0) {
        bottom = Math.min(bottom, top + maxHeight);
    }

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    if (width === 0 || height === 0) return;

    ui.spotlight.style.left = `${left}px`;
    ui.spotlight.style.top = `${top}px`;
    ui.spotlight.style.width = `${width}px`;
    ui.spotlight.style.height = `${height}px`;
    ui.spotlight.style.borderRadius = `${spotlightRadius}px`;
    ui.spotlight.style.opacity = '1';

    ui.tutorial.classList.add('portal-tutorial-overlay--spotlight-active');
}

function getPortalTutorialSpotlightPadding(step, isMobile = false) {
    const fallback = { top: 12, right: 12, bottom: 12, left: 12 };
    if (!step) return fallback;

    let rawPadding = step.spotlightPadding;

    if (rawPadding && typeof rawPadding === 'object' && !Array.isArray(rawPadding)) {
        if (isMobile && typeof rawPadding.mobile !== 'undefined') {
            rawPadding = rawPadding.mobile;
        } else if (!isMobile && typeof rawPadding.desktop !== 'undefined') {
            rawPadding = rawPadding.desktop;
        }
    }

    if (Number.isFinite(Number(rawPadding))) {
        const all = Number(rawPadding);
        return { top: all, right: all, bottom: all, left: all };
    }

    if (rawPadding && typeof rawPadding === 'object' && !Array.isArray(rawPadding)) {
        const top = Number.isFinite(Number(rawPadding.top)) ? Number(rawPadding.top) : fallback.top;
        const right = Number.isFinite(Number(rawPadding.right)) ? Number(rawPadding.right) : fallback.right;
        const bottom = Number.isFinite(Number(rawPadding.bottom)) ? Number(rawPadding.bottom) : fallback.bottom;
        const left = Number.isFinite(Number(rawPadding.left)) ? Number(rawPadding.left) : fallback.left;

        return { top, right, bottom, left };
    }

    return fallback;
}

function setPortalTutorialArrowDirection(direction = 'right') {
    const tutorial = document.getElementById('portal-tutorial-overlay');
    if (!tutorial) return;

    tutorial.classList.remove(
        'portal-tutorial-overlay--arrow-right',
        'portal-tutorial-overlay--arrow-left',
        'portal-tutorial-overlay--arrow-top',
        'portal-tutorial-overlay--arrow-bottom',
        'portal-tutorial-overlay--arrow-none'
    );

    const classMap = {
        right: 'portal-tutorial-overlay--arrow-left',
        left: 'portal-tutorial-overlay--arrow-right',
        top: 'portal-tutorial-overlay--arrow-bottom',
        bottom: 'portal-tutorial-overlay--arrow-top',
        none: 'portal-tutorial-overlay--arrow-none',
        center: 'portal-tutorial-overlay--arrow-none'
    };

    tutorial.classList.add(classMap[direction] || 'portal-tutorial-overlay--arrow-left');
}

function updatePortalTutorialCardPosition() {
    const ui = getPortalTutorialElements();
    if (!ui || !ui.card) return;

    const { tutorial, card } = ui;
    const steps = getPortalTutorialSteps();
    const currentStep = steps[currentPortalTutorialStepIndex];
    const uploadWrapper = document.getElementById('upload-wrapper');

    if (!currentStep) return;

    const targetElement = getPortalTutorialStepTargetElement(currentStep);
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isMobile = viewportWidth <= 992;

    if (!uploadWrapper || !uploadWrapper.classList.contains('loaded')) {
        clearPortalTutorialTargetHighlight();
        card.style.visibility = 'hidden';
        if (portalTutorialPositionRetryId) cancelAnimationFrame(portalTutorialPositionRetryId);
        portalTutorialPositionRetryId = requestAnimationFrame(updatePortalTutorialCardPosition);
        return;
    }

    if (portalTutorialPositionRetryId) {
        cancelAnimationFrame(portalTutorialPositionRetryId);
        portalTutorialPositionRetryId = null;
    }

    tutorial.classList.toggle('portal-tutorial-overlay--mobile', isMobile);

    const placement = isMobile
        ? (currentStep.placementMobile || 'center')
        : (currentStep.placementDesktop || 'right');

    if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const isOutOfViewport = rect.top < 0 || rect.bottom > viewportHeight || rect.left < 0 || rect.right > viewportWidth;

        // For top/bottom placements, also make sure there's room for the card itself,
        // not just the target — otherwise the card gets clamped and points at the wrong spot.
        // Measured from the padded highlight box (same box the card is actually anchored to),
        // not the raw target edges.
        let cardClearanceDeltaY = 0;
        if (placement === 'top' || placement === 'bottom') {
            const spotlightPadding = getPortalTutorialSpotlightPadding(currentStep, isMobile);
            const highlightTop = rect.top - spotlightPadding.top;
            const highlightBottom = rect.bottom + spotlightPadding.bottom;
            const cardHeightEstimate = card.offsetHeight || 260;
            const clearanceGap = 18 + 4 + 24; // arrow + desired gap + breathing room
            const needed = cardHeightEstimate + clearanceGap;

            if (placement === 'top' && highlightTop < needed) {
                cardClearanceDeltaY = -(needed - highlightTop);
            } else if (placement === 'bottom') {
                const spaceBelow = viewportHeight - highlightBottom;
                if (spaceBelow < needed) cardClearanceDeltaY = needed - spaceBelow;
            }
        }

        if (isOutOfViewport || cardClearanceDeltaY !== 0) {
            // body.portal-tutorial-open sets overflow:hidden to lock background scroll;
            // lift it just long enough for the scroll to actually take effect, then let
            // the CSS class govern it again. Always restore to '' (not a captured snapshot) —
            // overlapping calls (fast next/prev clicks) would otherwise capture the
            // temporary 'auto' value and get stuck unlocked.
            if (portalTutorialScrollUnlockRestoreId) {
                cancelAnimationFrame(portalTutorialScrollUnlockRestoreId);
            }

            document.body.style.overflow = 'auto';

            if (cardClearanceDeltaY !== 0) {
                window.scrollBy({ top: cardClearanceDeltaY, behavior: 'auto' });
            } else if (rect.height > viewportHeight) {
                // Centering a target taller than the viewport scrolls to its (invisible)
                // midpoint, hiding the top of it — align its top to the viewport instead.
                window.scrollBy({ top: rect.top - 24, behavior: 'auto' });
            } else {
                targetElement.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
            }

            portalTutorialScrollUnlockRestoreId = requestAnimationFrame(() => {
                portalTutorialScrollUnlockRestoreId = null;
                document.body.style.overflow = '';
            });
        }
    }

    setPortalTutorialTargetHighlight(currentStep);

    if (isMobile || !targetElement || placement === 'center') {
        card.style.left = '';
        card.style.top = '';
        card.style.width = '';
        card.style.visibility = 'visible';
        setPortalTutorialArrowDirection('none');
        return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    if (!targetRect || targetRect.width === 0 || targetRect.height === 0) {
        clearPortalTutorialTargetHighlight();
        card.style.visibility = 'hidden';
        if (portalTutorialPositionRetryId) cancelAnimationFrame(portalTutorialPositionRetryId);
        portalTutorialPositionRetryId = requestAnimationFrame(updatePortalTutorialCardPosition);
        return;
    }

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const arrowSize = 18;
    const desiredGap = 4;
    const gap = arrowSize + desiredGap;
    const cardWidth = Math.min(420, Math.max(300, viewportWidth - 96));
    const cardHeightFallback = Math.min(260, Math.max(180, viewportHeight - 120));

    // Clamp to the viewport (matching the spotlight's own clamping) so that a target
    // taller/wider than the viewport doesn't push the card off toward its unseen
    // center — the card follows the visible slice of the target, anchored near the top.
    const spotlightPadding = getPortalTutorialSpotlightPadding(currentStep, isMobile);
    const highlightLeft = Math.max(8, targetRect.left - spotlightPadding.left);
    const highlightTop = Math.max(8, targetRect.top - spotlightPadding.top);
    const highlightRight = Math.min(viewportWidth - 8, targetRect.right + spotlightPadding.right);
    const highlightMaxHeight = Number(currentStep.spotlightMaxHeight);
    const highlightBottom = (Number.isFinite(highlightMaxHeight) && highlightMaxHeight > 0)
        ? Math.min(viewportHeight - 8, targetRect.bottom + spotlightPadding.bottom, highlightTop + highlightMaxHeight)
        : Math.min(viewportHeight - 8, targetRect.bottom + spotlightPadding.bottom);
    const highlightRect = {
        left: highlightLeft,
        top: highlightTop,
        right: highlightRight,
        bottom: highlightBottom,
        width: Math.max(0, highlightRight - highlightLeft),
        height: Math.max(0, highlightBottom - highlightTop),
    };

    card.style.width = `${cardWidth}px`;
    const measuredCardWidth = card.offsetWidth || cardWidth;
    const measuredCardHeight = card.offsetHeight || cardHeightFallback;

    let left = 0;
    let top = 0;

    if (placement === 'left') {
        left = highlightRect.left - measuredCardWidth - gap;
        top = highlightRect.top + (highlightRect.height / 2) - (measuredCardHeight / 2);
    } else if (placement === 'right') {
        left = highlightRect.right + gap;
        top = highlightRect.top + (highlightRect.height / 2) - (measuredCardHeight / 2);
    } else if (placement === 'top') {
        left = highlightRect.left + (highlightRect.width / 2) - (measuredCardWidth / 2);
        top = highlightRect.top - measuredCardHeight - gap;
    } else if (placement === 'bottom') {
        left = highlightRect.left + (highlightRect.width / 2) - (measuredCardWidth / 2);
        top = highlightRect.bottom + gap;
    } else {
        left = highlightRect.left + (highlightRect.width / 2) - (measuredCardWidth / 2);
        top = highlightRect.top + (highlightRect.height / 2) - (measuredCardHeight / 2);
    }

    left = clamp(left, 24, viewportWidth - measuredCardWidth - 24);
    top = clamp(top, 24, viewportHeight - measuredCardHeight - 24);

    setPortalTutorialArrowDirection(placement);
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
    card.style.width = `${cardWidth}px`;
    card.style.visibility = 'visible';
}

function schedulePortalTutorialCardPositionUpdate() {
    if (portalTutorialPositionRetryId) {
        cancelAnimationFrame(portalTutorialPositionRetryId);
    }

    portalTutorialPositionRetryId = requestAnimationFrame(() => {
        portalTutorialPositionRetryId = null;
        updatePortalTutorialCardPosition();
    });
}

function renderPortalTutorialStep(stepIndex = 0) {
    const ui = getPortalTutorialElements();
    if (!ui) return;

    const steps = getPortalTutorialSteps();
    if (!steps.length) return;

    const clampedIndex = Math.max(0, Math.min(stepIndex, steps.length - 1));
    currentPortalTutorialStepIndex = clampedIndex;

    const currentStep = steps[currentPortalTutorialStepIndex];
    const translations = getPortalTutorialTranslations();
    const configuredTotal = Math.max(PORTAL_TUTORIAL_TOTAL_STEPS, steps.length);

    togglePortalTutorialUploadListPlaceholder(true);

    if (ui.stepLabel) {
        if (currentStep.isSeparateSpStep) {
            ui.stepLabel.textContent = translations.spTitle || 'SP-template';
        } else {
            ui.stepLabel.textContent = `${translations.stepPrefix} ${currentPortalTutorialStepIndex + 1}/${configuredTotal}`;
        }
    }

    if (ui.title) ui.title.textContent = currentStep.title || '';
    if (ui.text) ui.text.textContent = currentStep.text || '';

    if (ui.nextBtn) {
        const isLastStep = currentPortalTutorialStepIndex >= (steps.length - 1);
        ui.nextBtn.textContent = isLastStep ? translations.closeLabel : translations.nextLabel;
    }

    if (ui.prevBtn) {
        const isFirstStep = currentPortalTutorialStepIndex <= 0;
        ui.prevBtn.textContent = translations.previousLabel;
        ui.prevBtn.style.display = isFirstStep ? 'none' : '';
        ui.prevBtn.disabled = isFirstStep;
    }

    if (shouldPersistPortalTutorialProgress(currentPortalTutorialStepIndex)) {
        savePortalTutorialState({
            completed: false,
            completedAt: null,
            lastSeenStepIndex: currentPortalTutorialStepIndex
        });
    }

    schedulePortalTutorialCardPositionUpdate();
}

function openPortalTutorial(startStepIndex = 0, { isAutoOpen = false } = {}) {
    const ui = getPortalTutorialElements();
    if (!ui || !ui.tutorial) return;

    const resolvedStartIndex = Number.isFinite(Number(startStepIndex)) ? Number(startStepIndex) : 0;

    document.body.classList.add('portal-tutorial-open');
    if (ui.card) {
        ui.card.style.visibility = 'hidden';
    }

    ui.tutorial.classList.remove('hidden');
    ui.tutorial.classList.add('open');
    ui.tutorial.setAttribute('aria-hidden', 'false');
    ui.tutorial.classList.remove('portal-tutorial-overlay--enable-spotlight-anim');

    // isAutoSession marks this as the very-first-visit auto-triggered popup — only
    // that kind of open should come back by itself after a reload. A manual open
    // (info icon) should stay closed on the next reload.
    savePortalTutorialState({ isOpen: true, isAutoSession: isAutoOpen });

    renderPortalTutorialStep(resolvedStartIndex);

    requestAnimationFrame(() => {
        updatePortalTutorialCardPosition();
        const liveUi = getPortalTutorialElements();
        if (!liveUi || !liveUi.tutorial || !liveUi.tutorial.classList.contains('open')) return;
        liveUi.tutorial.classList.add('portal-tutorial-overlay--enable-spotlight-anim');
    });
}

function closePortalTutorial({ completed = false } = {}) {
    const ui = getPortalTutorialElements();
    if (!ui || !ui.tutorial) return;

    if (portalTutorialPositionRetryId) {
        cancelAnimationFrame(portalTutorialPositionRetryId);
        portalTutorialPositionRetryId = null;
    }

    clearPortalTutorialTargetHighlight();
    restorePortalTutorialForcedVisibility();
    togglePortalTutorialUploadListPlaceholder(false);

    document.body.classList.remove('portal-tutorial-open');

    ui.tutorial.classList.remove('open');
    ui.tutorial.classList.add('hidden');
    ui.tutorial.setAttribute('aria-hidden', 'true');
    ui.tutorial.classList.remove('portal-tutorial-overlay--enable-spotlight-anim');

    // Only finishing the last step marks it completed (resume restarts at step 1
    // after that). Closing early (X / backdrop) keeps completed false so the next
    // open — manual or auto-resumed — picks up at the step you left on.
    savePortalTutorialState({
        completed,
        completedAt: completed ? Date.now() : null,
        isOpen: false,
        lastSeenStepIndex: currentPortalTutorialStepIndex
    });
}

function goToNextPortalTutorialStep() {
    const steps = getPortalTutorialSteps();
    if (!steps.length) return;

    const isLastStep = currentPortalTutorialStepIndex >= (steps.length - 1);
    if (isLastStep) {
        closePortalTutorial({ completed: true });
        return;
    }

    renderPortalTutorialStep(currentPortalTutorialStepIndex + 1);
}

function goToPreviousPortalTutorialStep() {
    renderPortalTutorialStep(currentPortalTutorialStepIndex - 1);
}

// Backward-compatible alias for existing integrations.
function openPortalSidebarTutorial() {
    openPortalTutorial(0);
}

function restartPortalTutorial() {
    resetPortalTutorialState();
    openPortalTutorial(0);
}

window.openUploadPortalSidebarTutorial = openPortalSidebarTutorial;
window.openUploadPortalTutorial = openPortalTutorial;
window.restartUploadPortalTutorial = restartPortalTutorial;
window.resetUploadPortalTutorial = restartPortalTutorial;

window.addEventListener('resize', () => {
    if (document.body.classList.contains('portal-tutorial-open')) {
        schedulePortalTutorialCardPositionUpdate();
    }
});

document.addEventListener('click', (event) => {
    const closeTrigger = event.target.closest('[data-portal-tutorial-close]');
    if (closeTrigger) {
        closePortalTutorial();
        return;
    }

    if (event.target.classList.contains('portal-tutorial-overlay__backdrop')) {
        closePortalTutorial();
        return;
    }

    const nextTrigger = event.target.closest('[data-portal-tutorial-next]');
    if (nextTrigger) {
        goToNextPortalTutorialStep();
        return;
    }

    const prevTrigger = event.target.closest('[data-portal-tutorial-prev]');
    if (prevTrigger) {
        goToPreviousPortalTutorialStep();
        return;
    }

    const reopenTrigger = event.target.closest('#open-portal-tutorial-btn');
    if (reopenTrigger) {
        openPortalTutorial(getPortalTutorialResumeStepIndex());
        return;
    }

});

// First-visit tutorial auto-show is intentionally commented out for testing.
// if (!window.localStorage.getItem(PORTAL_TUTORIAL_KEY)) {
//     openPortalSidebarTutorial();
// }